import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { FikoGrup } from './FikoGrup';
import { FikoSahne } from './FikoSahne';
import { FikoVektor3D } from './FikoVektor3d';
// v2.0.0 - DimensionSettings entegrasyonu
import { DimensionSettings, type SettingsChangeEvent } from './fiko-olcum/dimension';

/**
 * ✅ CATIA TARZI PROFESYONEL ÖLÇÜM ÇİZGİSİ SİSTEMİ
 *
 * Her ölçüm türü için özel tasarım:
 * - mesafe: Extension lines + dimension line + oklar
 * - yaricap: Merkez noktadan yay üzerine çizgi + R yazısı
 * - cap: Merkez geçen çap çizgisi + Ø yazısı
 * - aci: Arc (yay) çizgisi + açı yazısı
 * - koordinat: Crosshair + koordinat yazısı
 * - alan: Merkez nokta + alan yazısı
 * - uzunluk: Çizgi boyunca ok-ok arası
 *
 * Sürüklenip taşınabilir (drag & drop)
 */

/**
 * Ölçüm türleri
 */
export type OlcumTuru =
    | 'mesafe'      // İki nokta arası mesafe
    | 'koordinat'   // Tek nokta koordinatları
    | 'cap'         // Çember/silindir çapı
    | 'yaricap'     // Çember/silindir yarıçapı
    | 'uzunluk'     // Çizgi uzunluğu
    | 'alan'        // Yüzey alanı
    | 'aci';        // Açı ölçümü

export class FikoOlcumCizgisi extends FikoGrup {
    override type: any = "FikoOlcumCizgisi";

    sabitBoyut = true;

    // ===== CATIA/IRONCAD ÖLÇÜM TARZI =====
    // 🎯 Siyah çizgiler, siyah yazı, profesyonel görünüm
    renkler = {
        cizgi: 0x000000,           // 🎯 SİYAH - ana boyut çizgisi
        ok: 0x000000,              // 🎯 SİYAH - solid filled ok başları
        yazi: '#000000',           // 🎯 SİYAH YAZI
        extensionLine: 0x000000,   // 🎯 SİYAH - extension çizgileri
        centerMark: 0x000000,      // 🎯 SİYAH - merkez işareti
        arcLine: 0x000000,         // 🎯 SİYAH - açı yayı
        radiusLine: 0x000000       // 🎯 SİYAH - yarıçap çizgisi
    };

    cizgiKalinligi: number = 1.5;  // 🎯 CATIA TARZI: Profesyonel kalınlıkta çizgi
    offsetMesafe: number = 15;     // 🎯 CATIA TARZI: Parçanın dışına çıkan offset
    olcumTuru: OlcumTuru = 'mesafe'; // Ölçüm türü

    // ===== SÜRÜKLEME ÖZELLİĞİ =====
    draggable: boolean = true;            // Sürüklenebilir mi?
    private _isDragging: boolean = false;  // Şu an sürükleniyor mu?

    /** 🎯 v2.1.0: Sürükleme durumunu dışarıdan kontrol et */
    get isDragging(): boolean { return this._isDragging; }
    private dragOffset: THREE.Vector3 = new THREE.Vector3(); // Sürükleme ofseti
    private originalOffset: number = 15;  // Orijinal offset mesafesi

    // ===== PERFORMANS OPTİMİZASYONLARI - CATIA SEVİYESİ =====
    private static materialPool = new Map<string, LineMaterial>(); // Material reuse
    private static geometryPool = new Map<string, LineGeometry>(); // Geometry pooling
    private static arrowMaterialPool = new Map<string, THREE.MeshBasicMaterial>(); // Arrow material pool
    private static arrowGeometryPool = new Map<string, THREE.ConeGeometry>(); // Arrow geometry pool

    // ⚡ PERFORMANS: SpriteMaterial pool - ayni ayarlar icin tek material
    private static spriteMaterialPool = new Map<string, THREE.SpriteMaterial>();

    private static getPooledSpriteMaterial(texture: THREE.Texture): THREE.SpriteMaterial {
        // SpriteMaterial texture-specific olmali, pool key olarak texture.id kullan
        const key = `sprite_${texture.id}`;
        let mat = FikoOlcumCizgisi.spriteMaterialPool.get(key);
        if (!mat) {
            mat = new THREE.SpriteMaterial({
                map: texture,
                depthTest: false,
                transparent: true,
            });
            FikoOlcumCizgisi.spriteMaterialPool.set(key, mat);
        }
        return mat;
    }
    private isDisposed = false; // Dispose flag

    // ⚡ PERFORMANS: Shared material/geometry accessor'lar
    /**
     * LineMaterial pooling - ayni renk+kalinlik+opacity icin tek material
     * 30+ allocation yerine 3-5 shared material
     */
    private static getPooledLineMaterial(color: number, linewidth: number, opacity: number = 1.0, dashed: boolean = false): LineMaterial {
        const key = `${color}_${linewidth}_${opacity}_${dashed ? 'd' : 's'}`;
        let mat = FikoOlcumCizgisi.materialPool.get(key);
        if (!mat) {
            mat = new LineMaterial({
                color,
                linewidth,
                depthTest: false,
                depthWrite: false,
                transparent: opacity < 1.0,
                opacity,
                fog: false,
                worldUnits: false,
                side: THREE.DoubleSide,
                toneMapped: false
            });
            if (dashed) {
                mat.dashed = true;
                mat.dashScale = 1;
                mat.dashSize = 2;
                mat.gapSize = 1.5;
            }
            FikoOlcumCizgisi.materialPool.set(key, mat);
        }
        return mat;
    }

    /**
     * Arrow MeshBasicMaterial pooling - ayni renk icin tek material
     */
    private static getPooledArrowMaterial(color: number): THREE.MeshBasicMaterial {
        const key = `arrow_${color}`;
        let mat = FikoOlcumCizgisi.arrowMaterialPool.get(key);
        if (!mat) {
            mat = new THREE.MeshBasicMaterial({
                color,
                depthTest: false,
                depthWrite: false,
                transparent: false,
                side: THREE.DoubleSide,
                toneMapped: false
            });
            FikoOlcumCizgisi.arrowMaterialPool.set(key, mat);
        }
        return mat;
    }

    /**
     * Arrow ConeGeometry pooling - ayni boyut icin tek geometry
     */
    private static getPooledConeGeometry(radius: number, height: number): THREE.ConeGeometry {
        const key = `cone_${radius.toFixed(3)}_${height.toFixed(3)}`;
        let geom = FikoOlcumCizgisi.arrowGeometryPool.get(key);
        if (!geom) {
            geom = new THREE.ConeGeometry(radius, height, 6);
            geom.translate(0, -height / 2, 0);
            FikoOlcumCizgisi.arrowGeometryPool.set(key, geom);
        }
        return geom;
    }

    /**
     * Tum material/geometry pool'larini temizle
     */
    static clearAllPools(): void {
        FikoOlcumCizgisi.materialPool.forEach(m => m.dispose());
        FikoOlcumCizgisi.materialPool.clear();
        FikoOlcumCizgisi.arrowMaterialPool.forEach(m => m.dispose());
        FikoOlcumCizgisi.arrowMaterialPool.clear();
        FikoOlcumCizgisi.arrowGeometryPool.forEach(g => g.dispose());
        FikoOlcumCizgisi.arrowGeometryPool.clear();
        FikoOlcumCizgisi.geometryPool.forEach(g => g.dispose());
        FikoOlcumCizgisi.geometryPool.clear();
        FikoOlcumCizgisi.spriteMaterialPool.forEach(m => m.dispose());
        FikoOlcumCizgisi.spriteMaterialPool.clear();
    }

    // v2.0.0 - DimensionSettings entegrasyonu
    private static _instances = new Set<FikoOlcumCizgisi>(); // Tüm aktif instance'lar
    private static _settingsListenerAdded = false; // Listener eklendi mi?

    /**
     * v2.0.0 - DimensionSettings değişikliklerini tüm instance'lara uygula
     */
    private static setupSettingsListener(): void {
        if (FikoOlcumCizgisi._settingsListenerAdded) return;

        try {
            const settings = DimensionSettings.getInstance();
            settings.addChangeListener((event: SettingsChangeEvent) => {
                // v2.0.0 DEBUG: Listener tetiklendi mi?
                console.log('[FikoOlcumCizgisi] Settings changed, updating', FikoOlcumCizgisi._instances.size, 'instances');

                // Tüm aktif instance'lara yeni ayarları uygula
                FikoOlcumCizgisi._instances.forEach(instance => {
                    if (!instance.isDisposed) {
                        // ayarlariUygula() içinde olcumTuruneGoreCiz() çağrılıyor
                        instance.ayarlariUygula();
                    }
                });
            });
            FikoOlcumCizgisi._settingsListenerAdded = true;
            console.log('[FikoOlcumCizgisi] Settings listener added successfully');
        } catch (e) {
            console.warn('[FikoOlcumCizgisi] Settings listener failed:', e);
        }
    }

    // 🚀 GPU-READY: Cone geometry pool (ok için) - her ölçüm aynı geometry'yi paylaşır
    private static _sharedConeGeometry: THREE.ConeGeometry | null = null;
    private static _sharedConeMaterial: THREE.MeshBasicMaterial | null = null;

    // 🚀 GPU-READY: Shared quaternion ve vector buffers
    private static readonly _tempQuaternion = new THREE.Quaternion();
    private static readonly _tempYukariYon = new THREE.Vector3(0, 1, 0);

    // 🚀 GPU-READY: Pre-allocated Vector3 buffers (zero-allocation inner loops)
    // hesaplaOffsetYonu() için reusable buffers - her frame'de allocation önlenir
    private static readonly _tempWorldNokta1 = new THREE.Vector3();
    private static readonly _tempWorldNokta2 = new THREE.Vector3();
    private static readonly _tempOrtaNokta = new THREE.Vector3();
    private static readonly _tempKameraVec = new THREE.Vector3();
    private static readonly _tempOffsetDir = new THREE.Vector3();
    private static readonly _tempUpVec = new THREE.Vector3();
    private static readonly _tempDirVec = new THREE.Vector3();
    private static readonly _tempOffset1 = new THREE.Vector3();
    private static readonly _tempOffset2 = new THREE.Vector3();

    // ===== ÇİZİM ELEMANLARI =====
    private cizgi: LineSegments2 | Line2 | null = null; // Ana boyut çizgisi
    private extensionLine1: LineSegments2 | null = null; // Uzatma çizgisi 1
    private extensionLine2: LineSegments2 | null = null; // Uzatma çizgisi 2
    private ok1: THREE.Mesh | null = null;
    private ok2: THREE.Mesh | null = null;
    private yazi: THREE.Sprite | null = null;

    // ===== ÖZEL ÇİZİM ELEMANLARI (Ölçüm türüne göre) =====
    private merkezIsareti: THREE.Group | null = null;     // Merkez crosshair (cap, yaricap için)
    private arcCizgi: Line2 | null = null;                // Açı yayı
    private radiusCizgi: LineSegments2 | null = null;     // Yarıçap çizgisi
    private koordinatCrosshair: THREE.Group | null = null; // Koordinat crosshair

    // ===== KONUM VERİLERİ =====
    private nokta1: THREE.Vector3;
    private nokta2: THREE.Vector3;
    private uzunluk: number = 0;

    /** 🎯 v3.3.1: Nokta1 getter (dialog uyumluluğu için) */
    get birinciNokta(): THREE.Vector3 { return this.nokta1; }
    /** 🎯 v3.3.1: Nokta2 getter (dialog uyumluluğu için) */
    get ikinciNokta(): THREE.Vector3 { return this.nokta2; }
    /** 🎯 v3.3.6: Yazi getter (klonlama için) */
    get yaziSprite(): THREE.Sprite | null { return this.yazi; }
    private hazirlaListenerEklendi: boolean = false; // Event listener flag
    private isArc: boolean = false; // Arc modu
    private acı: number = 0; // Arc açısı (derece cinsinden)
    private merkezNoktası: THREE.Vector3 = new THREE.Vector3(); // Arc merkezi
    private yariCap: number = 0; // Yarıçap (cap ve yaricap için)

    // 🔴 Event handler - Fiko3DYazi pattern'i
    private kontrolDegistiEvent = ($event: any) => this.kameraYonDegistiThrottled();

    // 🚀 PERFORMANS: Smooth scale interpolation için
    private lastKameraUpdateTime: number = 0;
    private kameraUpdateTimeout: number | null = null; // RAF → setTimeout (RAF queue overflow önlenir)
    private static readonly KAMERA_THROTTLE_MS: number = 100; // Zoom sırasında donmayı önler (32ms → 100ms)
    private targetScaleAdjustment: number = 1; // Hedef scale (interpolation için)
    private currentScaleAdjustment: number = 1; // Mevcut scale (smooth transition)
    private interpolationRAF: number | null = null; // Smooth interpolation RAF (her frame)

    // ===== SÜRÜKLEME EVENT HANDLERS =====
    private onPointerDown: ((event: PointerEvent) => void) | null = null;
    private onPointerMove: ((event: PointerEvent) => void) | null = null;
    private onPointerUp: ((event: PointerEvent) => void) | null = null;

    constructor(
        public override sahne?: FikoSahne,
        name?: string,
        nokta1: THREE.Vector3 = new THREE.Vector3(0, 0, 0),
        nokta2: THREE.Vector3 = new THREE.Vector3(1, 0, 0),
        uzunluk: number = 0,
        renkler?: { cizgi?: number, ok?: number, yazi?: string },
        isArc: boolean = false,
        acı: number = 0,
        merkezNoktası?: THREE.Vector3,
        cizgiKalinligi: number = 2,
        olcumTuru: OlcumTuru = 'mesafe'
    ) {
        super(sahne, name || 'OlcumCizgisi');

        // 🔴 Merkezi ayarları yükle (localStorage'dan)
        const merkeziAyarlar = FikoOlcumCizgisi.getOlcumAyarlari();

        // Ayarları uygula (constructor parametresi > merkezi ayar > varsayılan)
        this.cizgiKalinligi = cizgiKalinligi !== 2 ? cizgiKalinligi : merkeziAyarlar.cizgiKalinligi;
        this.offsetMesafe = merkeziAyarlar.offsetMesafe;
        this.originalOffset = merkeziAyarlar.offsetMesafe;

        // Renkleri merkezi ayarlardan yükle
        this.renkler = {
            cizgi: merkeziAyarlar.renkler.cizgi,
            ok: merkeziAyarlar.renkler.ok,
            yazi: merkeziAyarlar.renkler.yazi,
            extensionLine: merkeziAyarlar.renkler.extensionLine,
            centerMark: merkeziAyarlar.renkler.centerMark,
            arcLine: merkeziAyarlar.renkler.arcLine,
            radiusLine: merkeziAyarlar.renkler.radiusLine
        };

        this.olcumTuru = olcumTuru;

        // Grup position'unu nokta1'e ayarla (world space'te doğru konumda)
        this.position.copy(nokta1);

        // Local koordinatlar: nokta2'yi nokta1'den başlayarak hesapla
        this.nokta1 = new THREE.Vector3(0, 0, 0); // Local origin
        this.nokta2 = new THREE.Vector3().subVectors(nokta2, nokta1); // nokta2 - nokta1
        this.uzunluk = uzunluk;

        // Yarıçap hesapla (cap ve yaricap için)
        this.yariCap = this.nokta1.distanceTo(this.nokta2);

        // Arc parametreleri
        this.isArc = isArc;
        this.acı = acı;
        if (merkezNoktası) {
            // merkezNoktası world koordinatlarda, bunu local'e çevir
            this.merkezNoktası = new THREE.Vector3().subVectors(merkezNoktası, nokta1); // Merkez de local
        }

        // Renkler özelleştir
        if (renkler) {
            this.renkler.cizgi = renkler.cizgi ?? this.renkler.cizgi;
            this.renkler.ok = renkler.ok ?? this.renkler.ok;
            this.renkler.yazi = renkler.yazi ?? this.renkler.yazi;
        }

        this.renderOrder = Infinity; // 🔴 BUGFIX: En üstte render (sortObjects'ten bağımsız)

        // 🔴 KRİTİK: Frustum culling'i devre dışı bırak (ekranın dışına çıksa da görünür olsun)
        this.frustumCulled = false;

        // 🔴 BUGFIX: Bounding sphere'yi sonsuz yap (en önemli!)
        const infiniteSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);
        Object.defineProperty(this, 'boundingSphere', {
            get() {
                return infiniteSphere;
            },
            configurable: true
        });

        // ===== ÖLÇÜM TÜRÜNE GÖRE ÇİZİM =====
        this.olcumTuruneGoreCiz();

        // 🎯 v2.2.0: olcumTuru ve koordinat verilerini userData'ya kaydet (dialog uyumluluğu için)
        this.userData.olcumTuru = olcumTuru;
        if (olcumTuru === 'koordinat') {
            // Koordinat ölçümü için world pozisyonu kaydet
            this.userData.koordinatNoktasi = {
                x: nokta1.x,
                y: nokta1.y,
                z: nokta1.z
            };
        }

        this.userData.FikoArayuz.agactaGorunur = true;
        this.userData.FikoArayuz.listeGoster = false;
        this.userData.FikoArayuz.simge = "./simgeler/ruler-1.svg";

        // Constructor'de hazirla() çağır (FikoEksen gibi)
        if (sahne) {
            this.hazirla(sahne);
        }

        // v2.0.0 - DimensionSettings entegrasyonu: Instance'ı kaydet
        FikoOlcumCizgisi._instances.add(this);
        FikoOlcumCizgisi.setupSettingsListener();
        console.log('[FikoOlcumCizgisi] Instance registered, total:', FikoOlcumCizgisi._instances.size);
    }

    /**
     * ⚡ PERFORMANS: In-place nokta güncelleme - destroy+recreate yerine
     * Constructor overhead'ini atlar (super, localStorage, _instances, event listener)
     * Sadece geometri + yazı güncellenir, sahne yapısı korunur.
     *
     * @param yeniNokta1 World space başlangıç noktası
     * @param yeniNokta2 World space bitiş noktası
     * @param yeniUzunluk İki nokta arası uzunluk
     * @param yeniLabel Güncellenmiş yazı metni (opsiyonel - verilmezse yazı güncellenmez)
     * @param yeniMerkezNoktasi Arc/cap için merkez noktası (opsiyonel)
     */
    noktaGuncelle(
        yeniNokta1: THREE.Vector3,
        yeniNokta2: THREE.Vector3,
        yeniUzunluk: number,
        yeniLabel?: string,
        yeniMerkezNoktasi?: THREE.Vector3
    ): void {
        if (this.isDisposed) return;

        // World position güncelle
        this.position.copy(yeniNokta1);

        // Local koordinatlar güncelle
        this.nokta1.set(0, 0, 0);
        this.nokta2.subVectors(yeniNokta2, yeniNokta1);
        this.uzunluk = yeniUzunluk;
        this.yariCap = this.nokta1.distanceTo(this.nokta2);

        // Merkez noktası güncelle (cap/yaricap/aci için)
        if (yeniMerkezNoktasi) {
            this.merkezNoktası.subVectors(yeniMerkezNoktasi, yeniNokta1);
        }

        // Geometri yeniden çiz (temizle + oluştur)
        this.temizleElemanlar();
        this.olcumTuruneGoreCiz();

        // Yazı güncelle (labelYenile ile canvas reuse)
        if (yeniLabel !== undefined && this.userData.yaziParams) {
            const p = this.userData.yaziParams;
            this.yaziEkle(yeniLabel, undefined, p.fontSize, p.textColor, p.backgroundColor, p.backgroundAlpha, p.offsetFactor);
        }

        // Matrix güncelle
        this.updateMatrixWorld(true);
    }

    /**
     * ✅ ÖLÇÜM TÜRÜNE GÖRE ÇİZİM - CATIA TARZI
     */
    private olcumTuruneGoreCiz(): void {
        switch (this.olcumTuru) {
            case 'mesafe':
            case 'uzunluk':
                if (this.isArc) {
                    this.arcOlustur();
                } else {
                    this.cizgiOlustur();
                }
                this.okOlustur();
                break;

            case 'yaricap':
                this.yaricapCizgiOlustur();
                break;

            case 'cap':
                this.capCizgiOlustur();
                break;

            case 'aci':
                this.aciArcOlustur();
                break;

            case 'koordinat':
                this.koordinatCrosshairOlustur();
                break;

            case 'alan':
                this.alanIsaretiOlustur();
                break;

            default:
                this.cizgiOlustur();
                this.okOlustur();
                break;
        }
    }

    /**
     * ✅ Offset yönünü hesapla - Kameraya göre akıllı offset
     * 🚀 GPU-READY: Zero-allocation - pre-allocated static buffers kullanır
     */
    private hesaplaOffsetYonu(dirVec: THREE.Vector3): THREE.Vector3 {
        // 🚀 Static buffer'ları kullan (allocation yok)
        const offsetDir = FikoOlcumCizgisi._tempOffsetDir;

        if (this.sahne && this.sahne.anaGosterim && this.sahne.anaGosterim.aktifKamera) {
            // Kamera varsa: Kameradan çizgiye dik yön
            const kameraPos = this.sahne.anaGosterim.aktifKamera.position;

            // 🚀 Pre-allocated buffers kullan
            const worldNokta1 = FikoOlcumCizgisi._tempWorldNokta1.copy(this.nokta1).applyMatrix4(this.matrixWorld);
            const worldNokta2 = FikoOlcumCizgisi._tempWorldNokta2.copy(this.nokta2).applyMatrix4(this.matrixWorld);
            const ortaNokta = FikoOlcumCizgisi._tempOrtaNokta.addVectors(worldNokta1, worldNokta2).multiplyScalar(0.5);

            // 🚀 Pre-allocated buffer kullan
            const kameraVec = FikoOlcumCizgisi._tempKameraVec.subVectors(ortaNokta, kameraPos).normalize();

            // Çizgiye ve kameraya dik yön (cross product)
            offsetDir.crossVectors(dirVec, kameraVec).normalize();

            // Eğer offset çok küçükse (paralel durumu), ekran yukarı yönünü kullan
            if (offsetDir.lengthSq() < 0.01) {
                const upVec = FikoOlcumCizgisi._tempUpVec.copy(this.sahne.anaGosterim.aktifKamera.up).normalize();
                offsetDir.crossVectors(dirVec, upVec).normalize();
            }

            // Offset yönünü kameraya doğru çevir (her zaman ekrana doğru)
            if (offsetDir.dot(kameraVec) < 0) {
                offsetDir.negate();
            }
        } else {
            // Kamera yoksa: Basit yaklaşım - Yukarı yön
            offsetDir.set(0, 1, 0);

            if (Math.abs(dirVec.dot(offsetDir)) > 0.9) {
                offsetDir.set(1, 0, 0);
            }

            // 🚀 Inline cross product (allocation yok)
            offsetDir.crossVectors(dirVec, FikoOlcumCizgisi._tempUpVec.set(0, 0, 1)).normalize();
            if (offsetDir.lengthSq() < 0.01) {
                offsetDir.crossVectors(dirVec, FikoOlcumCizgisi._tempUpVec.set(0, 1, 0)).normalize();
            }
        }

        // 🚀 Sonucu clone() ile döndür (caller'ın değiştirmesi için)
        // NOT: Eğer caller değiştirmiyorsa, doğrudan offsetDir dönülebilir (daha hızlı)
        return offsetDir.clone();
    }

    /**
     * ✅ Çizgiyi oluştur - Catia tarzı boyut çizgisi (ölçüm türüne göre farklı gösterim)
     * BUGFIX: Error handling ve validation eklendi
     */
    private cizgiOlustur(): void {
        try {
            // Eski çizgileri temizle
            if (this.cizgi) {
                this.remove(this.cizgi);
                (this.cizgi.geometry as LineGeometry).dispose();
                ((this.cizgi.material) as LineMaterial).dispose();
                this.cizgi = null;
            }
            if (this.extensionLine1) {
                this.remove(this.extensionLine1);
                (this.extensionLine1.geometry as LineGeometry).dispose();
                ((this.extensionLine1.material) as LineMaterial).dispose();
                this.extensionLine1 = null;
            }
            if (this.extensionLine2) {
                this.remove(this.extensionLine2);
                (this.extensionLine2.geometry as LineGeometry).dispose();
                ((this.extensionLine2.material) as LineMaterial).dispose();
                this.extensionLine2 = null;
            }

            // Noktaları valide et
            if (!this.nokta1 || !this.nokta2) {
                return;
            }

            // NaN kontrol et
            const isNaN = (v: THREE.Vector3) => !isFinite(v.x) || !isFinite(v.y) || !isFinite(v.z);
            if (isNaN(this.nokta1) || isNaN(this.nokta2)) {
                return;
            }

            // Ölçüm türüne göre farklı gösterim
            if (this.olcumTuru === 'koordinat') {
                // Koordinat ölçümü - Sadece nokta işareti (çizgi yok)
                return;
            } else if (this.olcumTuru === 'alan') {
                // Alan ölçümü - Sadece yazı (çizgi yok)
                return;
            }

            // ✅ CATIA TARZI AKILLI OFFSET HESAPLAMA
            // 🚀 GPU-READY: Pre-allocated buffers kullan
            // 1. İki nokta arasındaki vektör
            const dirVec = FikoOlcumCizgisi._tempDirVec.subVectors(this.nokta2, this.nokta1).normalize();

            // 2. Offset yönünü kameraya göre hesapla (helper metod ile)
            let offsetDir = this.hesaplaOffsetYonu(dirVec);

            // 🔴 BUGFIX: Offset yönünü her zaman yukarı doğru tut (Y+ yönünde)
            // Eğer offset aşağı yönlüyse (Y < 0), ters çevir
            if (offsetDir.y < 0) {
                offsetDir.negate();
            }

            // 3. Offset noktalarını hesapla
            // 🚀 GPU-READY: Pre-allocated buffers kullan (offsetDir zaten clone() ile döndü)
            const offset1Local = FikoOlcumCizgisi._tempOffset1.copy(this.nokta1).addScaledVector(offsetDir, this.offsetMesafe);
            const offset2Local = FikoOlcumCizgisi._tempOffset2.copy(this.nokta2).addScaledVector(offsetDir, this.offsetMesafe);

            // Ana boyut çizgisi (offset'te) - LOCAL KOORDİNATLARDA
            const dimLineGeometry = new LineGeometry();
            dimLineGeometry.setPositions([
                offset1Local.x, offset1Local.y, offset1Local.z,
                offset2Local.x, offset2Local.y, offset2Local.z
            ]);

            // ⚡ PERFORMANS: Pooled material (ayni renk+kalinlik = shared)
            const material = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi);

            this.cizgi = new LineSegments2(dimLineGeometry, material);
            this.cizgi.name = 'DimensionLine';
            this.cizgi.renderOrder = Infinity;
            this.cizgi.computeLineDistances();
            dimLineGeometry.computeBoundingBox();
            dimLineGeometry.computeBoundingSphere();
            if (dimLineGeometry.boundingSphere) {
                dimLineGeometry.boundingSphere.radius = Infinity;
            }
            this.cizgi.frustumCulled = false;
            this.cizgi.visible = true;
            this.add(this.cizgi);

            // 🎯 CATIA/IRONCAD: Extension line 1 - kısa gap ile
            // Extension line ok başından biraz içeride başlar ve parça kenarından biraz önce biter
            const GAP_RATIO = 0.08; // Parça kenarından %8 gap
            const ext1Geometry = new LineGeometry();

            // Gap hesapla: extension line parça kenarına kadar gitmez, kısa bir gap bırakır
            const ext1Bitis = FikoOlcumCizgisi._tempOffset1.copy(this.nokta1).addScaledVector(offsetDir, this.offsetMesafe * GAP_RATIO);

            ext1Geometry.setPositions([
                offset1Local.x, offset1Local.y, offset1Local.z,
                ext1Bitis.x, ext1Bitis.y, ext1Bitis.z  // Parça kenarından kısa gap ile
            ]);

            // 🎯 CATIA/IRONCAD PROFESYONEL: İnce solid extension line
            // Kesikli değil, ince düz çizgi - endüstriyel CAD standardı
            const extMaterial1 = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.extensionLine, this.cizgiKalinligi * 0.5, 0.8);

            this.extensionLine1 = new LineSegments2(ext1Geometry, extMaterial1);
            this.extensionLine1.name = 'ExtensionLine1';
            this.extensionLine1.renderOrder = Infinity;
            this.extensionLine1.computeLineDistances();
            ext1Geometry.computeBoundingBox();
            ext1Geometry.computeBoundingSphere();
            if (ext1Geometry.boundingSphere) {
                ext1Geometry.boundingSphere.radius = Infinity;
            }
            this.extensionLine1.frustumCulled = false;
            this.extensionLine1.visible = true;
            this.add(this.extensionLine1);

            // 🎯 CATIA/IRONCAD: Extension line 2 - kısa gap ile
            const ext2Geometry = new LineGeometry();

            // Gap hesapla: extension line parça kenarına kadar gitmez, kısa bir gap bırakır
            const ext2Bitis = FikoOlcumCizgisi._tempOffset2.copy(this.nokta2).addScaledVector(offsetDir, this.offsetMesafe * GAP_RATIO);

            ext2Geometry.setPositions([
                offset2Local.x, offset2Local.y, offset2Local.z,
                ext2Bitis.x, ext2Bitis.y, ext2Bitis.z  // Parça kenarından kısa gap ile
            ]);

            // 🎯 CATIA/IRONCAD PROFESYONEL: İnce solid extension line
            const extMaterial2 = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.extensionLine, this.cizgiKalinligi * 0.5, 0.8);

            this.extensionLine2 = new LineSegments2(ext2Geometry, extMaterial2);
            this.extensionLine2.name = 'ExtensionLine2';
            this.extensionLine2.renderOrder = Infinity;
            this.extensionLine2.computeLineDistances();
            ext2Geometry.computeBoundingBox();
            ext2Geometry.computeBoundingSphere();
            if (ext2Geometry.boundingSphere) {
                ext2Geometry.boundingSphere.radius = Infinity;
            }
            this.extensionLine2.frustumCulled = false;
            this.extensionLine2.visible = true;
            this.add(this.extensionLine2);

        } catch (e) {
            this.cizgi = null;
        }
    }

    /**
     * ✅ Arc (Yay) oluştur - Açılı ölçümler için CAD tarzı yay (LineSegments2 ile kalın çizgi)
     * BUGFIX: Error handling ve validation eklendi
     */
    private arcOlustur(): void {
        try {
            // Eski çizgiyi temizle
            if (this.cizgi) {
                this.remove(this.cizgi);
                (this.cizgi.geometry as LineGeometry).dispose();
                ((this.cizgi.material) as LineMaterial).dispose();
                this.cizgi = null;
            }

            // Noktaları valide et
            if (!this.nokta1 || !this.nokta2) {
                return;
            }

            // NaN kontrol et
            const isNaN = (v: THREE.Vector3) => !isFinite(v.x) || !isFinite(v.y) || !isFinite(v.z);
            if (isNaN(this.nokta1) || isNaN(this.nokta2)) {
                return;
            }

            // Basit arc: nokta1'den nokta2'ye perpendicular arc çiz
            // nokta1 ve nokta2 arasında orta nokta bul
            const ortaNokta = new THREE.Vector3().addVectors(this.nokta1, this.nokta2).multiplyScalar(0.5);

            // Perpendicular yön hesapla (nokta1-nokta2 vektörüne dik)
            const dir = new THREE.Vector3().subVectors(this.nokta2, this.nokta1).normalize();

            // Perpendicular vektör (X-Y düzlemde dik yön)
            const perpendicular = new THREE.Vector3(-dir.z, 0, dir.x).normalize();

            // Arc merkezi perpendicular yönde
            const mesafe = this.nokta1.distanceTo(this.nokta2);
            const arcMerkezi = new THREE.Vector3().addVectors(ortaNokta, perpendicular.multiplyScalar(mesafe * 0.2));

            // Arc noktaları oluştur
            const segments = Math.max(30, Math.ceil(Math.abs(this.acı) / 2));
            const points: THREE.Vector3[] = [];

            for (let i = 0; i <= segments; i++) {
                const t = i / segments;
                const point = new THREE.Vector3()
                    .lerpVectors(this.nokta1, this.nokta2, t)
                    .lerp(arcMerkezi, 0.3 * Math.sin(t * Math.PI)); // Perpendicular bulge

                points.push(point);
            }

            // LineGeometry oluştur
            const lineGeometry = new LineGeometry();
            const positions: number[] = [];
            points.forEach(p => {
                positions.push(p.x, p.y, p.z);
            });

            lineGeometry.setPositions(positions);

            // LineMaterial oluştur - Pixel-based kalınlık desteği
            const material = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi);

            // LineSegments2 oluştur
            this.cizgi = new LineSegments2(lineGeometry, material);
            this.cizgi.name = 'OlcumArc';
            this.cizgi.renderOrder = Infinity;  // 🔴 BUGFIX: En üstte render

            // 🔴 KRİTİK: computeLineDistances() çağrısı ZORUNLU!
            this.cizgi.computeLineDistances();

            // 🔴 KRİTİK: Bounding box güncelle (frustum culling sorunu için)
            lineGeometry.computeBoundingBox();
            lineGeometry.computeBoundingSphere();

            // 🔴 KRİTİK: Bounding sphere'yi sonsuz yap (kamera her açıda görsün)
            if (lineGeometry.boundingSphere) {
                lineGeometry.boundingSphere.radius = Infinity;
            }

            // Frustum culling'i devre dışı bırak (ekranın dışına çıksa da görünür olsun)
            this.cizgi.frustumCulled = false;

            this.add(this.cizgi);

        } catch (e) {
            this.cizgi = null;
        }
    }

    /**
     * ✅ Okları oluştur - Boyut çizgisinin uçlarında oklar (Catia tarzı)
     * 🚀 GPU-READY: Pre-allocated buffers ve shared geometry kullanır
     */
    private okOlustur(): void {
        // Eski okları temizle
        // 🚀 NOT: Geometry shared olduğu için dispose etmiyoruz, sadece material
        if (this.ok1) {
            this.remove(this.ok1);
            // Geometry shared - dispose etme!
            ((this.ok1 as THREE.Mesh).material as THREE.Material).dispose();
        }
        if (this.ok2) {
            this.remove(this.ok2);
            // Geometry shared - dispose etme!
            ((this.ok2 as THREE.Mesh).material as THREE.Material).dispose();
        }

        // Koordinat ve alan ölçümü için ok yok
        if (this.olcumTuru === 'koordinat' || this.olcumTuru === 'alan') {
            return;
        }

        // 🎯 CATIA/IRONCAD TARZI: Geniş, profesyonel ok başları
        // Çizginin uçlarında, solid filled, görünür boyut
        const mesafe = this.nokta1.distanceTo(this.nokta2);
        const OK_RADIUS = Math.max(0.2, Math.min(0.5, mesafe * 0.012)); // 🎯 Geniş radius - profesyonel
        const OK_HEIGHT = Math.max(0.5, Math.min(1.2, mesafe * 0.028)); // 🎯 Uzun ok - CATIA tarzı

        // Arc ise, okları çizgi doğrultusunda başlangıç ve bitiş yakınında koy
        if (this.isArc) {
            // 🚀 Pre-allocated buffer kullan
            const dir = FikoOlcumCizgisi._tempDirVec.subVectors(this.nokta2, this.nokta1).normalize();

            // Ok 1 - nokta1 yakınında (geri yönü işaret)
            // okOlusturHelper position'ı kopyalayacak, clone gerekli
            this.ok1 = this.okOlusturHelper(this.nokta1.clone(), dir.clone().multiplyScalar(-1), OK_RADIUS, OK_HEIGHT);
            this.ok1.name = 'OlcumOk1';
            this.add(this.ok1);

            // Ok 2 - nokta2 yakınında (ileri yönü işaret)
            this.ok2 = this.okOlusturHelper(this.nokta2.clone(), dir.clone(), OK_RADIUS, OK_HEIGHT);
            this.ok2.name = 'OlcumOk2';
            this.add(this.ok2);
        } else {
            // 🚀 Pre-allocated buffer kullan
            const dirVec = FikoOlcumCizgisi._tempDirVec.subVectors(this.nokta2, this.nokta1).normalize();

            // ✅ Offset hesaplama (cizgiOlustur ile AYNI mantık - kameraya göre)
            let offsetDir = this.hesaplaOffsetYonu(dirVec);

            // 🔴 BUGFIX: Offset yönünü her zaman yukarı doğru tut (Y+ yönünde)
            if (offsetDir.y < 0) {
                offsetDir.negate();
            }

            // 🚀 Pre-allocated buffers kullan
            const offset1 = FikoOlcumCizgisi._tempOffset1.copy(this.nokta1).addScaledVector(offsetDir, this.offsetMesafe);
            const offset2 = FikoOlcumCizgisi._tempOffset2.copy(this.nokta2).addScaledVector(offsetDir, this.offsetMesafe);

            // Ok 1 - offset1 konumunda, dimension line boyunca sağa doğru
            // okOlusturHelper position ve yön'ü kullanacak, clone gerekli
            this.ok1 = this.okOlusturHelper(offset1.clone(), dirVec.clone(), OK_RADIUS, OK_HEIGHT);
            this.ok1.name = 'OlcumOk1';
            this.add(this.ok1);

            // Ok 2 - offset2 konumunda, dimension line boyunca sola doğru
            this.ok2 = this.okOlusturHelper(offset2.clone(), dirVec.clone().multiplyScalar(-1), OK_RADIUS, OK_HEIGHT);
            this.ok2.name = 'OlcumOk2';
            this.add(this.ok2);
        }
    }

    /**
     * ✅ PROFESYONEL Ok Mesh'i oluştur - CATIA/IronCAD tarzı solid filled arrowhead
     * Ok, verilen yöne doğru bakacak şekilde yönlendirilir ve çizgiye yapışık durur
     * 🎯 INDUSTRIAL STANDARD: Keskin, kompakt, solid filled üçgen ok başları
     * 🚀 GPU-READY: Her ok için dinamik geometry (boyut ölçeklenir)
     */
    private okOlusturHelper(nokta: THREE.Vector3, yon: THREE.Vector3, radius: number, height: number): THREE.Mesh {
        // 🎯 CATIA/IRONCAD: Her ok için doğru boyutta geometry oluştur
        // Shared geometry yerine parametrik boyut - profesyonel görünüm için kritik
        const coneGeometry = FikoOlcumCizgisi.getPooledConeGeometry(radius, height);

        // 🎯 SOLID FILLED: MeshBasicMaterial ile dolu siyah ok başı
        // Depth test kapalı = her zaman en üstte görünür
        const okMaterial = FikoOlcumCizgisi.getPooledArrowMaterial(this.renkler.ok);

        const ok = new THREE.Mesh(coneGeometry, okMaterial);

        // Ok pozisyonunu nokta konumuna ayarla
        ok.position.copy(nokta);

        // 🚀 Pre-allocated quaternion kullan (allocation yok)
        FikoOlcumCizgisi._tempQuaternion.setFromUnitVectors(FikoOlcumCizgisi._tempYukariYon, yon);
        ok.quaternion.copy(FikoOlcumCizgisi._tempQuaternion);

        ok.renderOrder = Infinity;
        ok.frustumCulled = false;

        return ok;
    }

    /**
     * ✅ CATIA/IRONCAD YAZI STİLİ - SİYAH YAZI + BEYAZ SHADOW
     * 🎯 Siyah yazı, beyaz gölge ile maksimum okunabilirlik
     */
    yaziEkle(metin: string, konum?: THREE.Vector3, fontSize: number = 12, textColor?: string, backgroundColor: string = '', backgroundAlpha: number = 0, offsetFactor: number = 0.1): void {
        const yaziRengi = textColor ?? this.renkler.yazi ?? '#000000';

        // ⚡ PERFORMANS: Mevcut sprite varsa ve canvas referansi gecerliyse yeniden kullan
        if (this.yazi?.userData?._labelCanvas) {
            this.labelYenile(this.yazi, {
                text: metin,
                textColor: yaziRengi,
                backgroundColor: backgroundColor,
                backgroundAlpha: backgroundAlpha,
                shadow: false,
                fontSize: fontSize,
                padding: 2,
                scaleFactor: 0.04
            });

            // Yazı parametrelerini güncelle
            this.userData.yaziParams = { metin, fontSize, textColor, backgroundColor, backgroundAlpha, offsetFactor };

            // originalScale'i yeniden hesapla (scale degismis olabilir)
            this.yazi.userData.originalScale = new THREE.Vector3().copy(this.yazi.scale);

            // Pozisyonu güncelle
            this._yaziPozisyonGuncelle(konum, fontSize);

            if (this.sabitBoyut && this.sahne) {
                this.kameraYonDegisti(this, true);
            }
            return;
        }

        // 🎯 v3.3.6 FIX: originalScale'i koru (mevcut scale değil!)
        let oncekiOriginalScale: THREE.Vector3 | null = null;
        if (this.yazi?.userData?.originalScale) {
            oncekiOriginalScale = (this.yazi.userData.originalScale as THREE.Vector3).clone();
        }

        // Eski yazıyı temizle
        if (this.yazi) {
            this.remove(this.yazi);
            const texture = (this.yazi.material as THREE.SpriteMaterial).map;
            if (texture) texture.dispose();
            ((this.yazi as THREE.Sprite).material as THREE.Material).dispose();
        }

        // 🎯 v3.3.4 FIX: Clone sonrası this.yazi null olabilir ama children'da sprite var
        const spritelarTemizle: THREE.Sprite[] = [];
        this.children.forEach(child => {
            if (child instanceof THREE.Sprite) {
                if (!oncekiOriginalScale && child.userData?.originalScale) {
                    oncekiOriginalScale = (child.userData.originalScale as THREE.Vector3).clone();
                }
                spritelarTemizle.push(child);
            }
        });
        spritelarTemizle.forEach(sprite => {
            this.remove(sprite);
            const tex = (sprite.material as THREE.SpriteMaterial).map;
            if (tex) tex.dispose();
            (sprite.material as THREE.Material).dispose();
        });

        if (oncekiOriginalScale) {
            this.userData._oncekiOriginalScale = oncekiOriginalScale;
        }

        this.userData.yaziParams = { metin, fontSize, textColor, backgroundColor, backgroundAlpha, offsetFactor };

        // 🎯 PROFESYONEL YAZI - Gölgesiz, sade
        this.yazi = this.labelOlustur({
            text: metin,
            textColor: yaziRengi,
            backgroundColor: backgroundColor,
            backgroundAlpha: backgroundAlpha,
            offsetFactor: offsetFactor,
            shadow: false,
            fontSize: fontSize,
            padding: 2,
            scaleFactor: 0.04
        });

        this._yaziPozisyonGuncelle(konum, fontSize);

        this.yazi.renderOrder = Infinity;  // 🔴 BUGFIX: En üstte render

        // 🔴 Frustum culling'i devre dışı bırak (ekranın dışında da görünür olsun)
        this.yazi.frustumCulled = false;

        this.add(this.yazi);

        // 🎯 v3.3.6 FIX: originalScale'i koru ve kamera scale'ini uygula
        if (this.userData._oncekiOriginalScale) {
            // Önceki originalScale'i koru (büyüme birikimini önle)
            this.yazi.userData.originalScale = this.userData._oncekiOriginalScale.clone();
            delete this.userData._oncekiOriginalScale; // Temizle

            // Kamera scale'ini uygula (originalScale üzerinden hesaplanır)
            if (this.sabitBoyut && this.sahne) {
                this.kameraYonDegisti(this, true);
            }
        } else {
            // 🔴 KRITIK: İlk kez - original scale'i sakla (inverse scaling için)
            this.yazi.userData.originalScale = new THREE.Vector3().copy(this.yazi.scale);

            // Kamera scale'ini uygula
            if (this.sabitBoyut && this.sahne) {
                this.kameraYonDegisti(this, true);
            }
        }

        // 🔴 KRITIK: Event listener'ı hazirla() ile güncelle
        // yaziEkle çağrıldığında yazı yeniden oluşturuluyor, listener'ı da yeniden kurmalıyız
        if (this.sahne && !this.hazirlaListenerEklendi) {
            this.hazirla(this.sahne);
        }
    }

    /**
     * ✅ CATIA/IRONCAD Label - Profesyonel Yazı
     * 🎯 Siyah yazı, gölgesiz, şeffaf arka plan
     */
    private labelOlustur({
        text,
        textColor = '#000000',   // 🎯 Siyah yazı
        backgroundColor,
        backgroundAlpha = 0,     // 🎯 Arka plan yok
        offsetFactor = 0.1,
        borderColor = '',        // 🎯 Kenarlık yok
        borderWidth = 0,
        borderRadius = 0,
        fontSize = 11,           // 🎯 Kompakt font
        minFontSize = 9,
        fontFamily = 'Arial, Helvetica, sans-serif',
        padding = 2,             // 🎯 Minimal padding
        shadow = false,          // 🎯 Gölge kapalı
        shadowOptions = { blur: 0, offsetX: 0, offsetY: 0, color: 'transparent' },
        glow = false,
        glowOptions = { color: '#000', blur: 0 },
        scaleFactor = 0.035,
    }: {
        text: string,
        textColor?: string,
        backgroundColor?: string,
        backgroundAlpha?: number,
        offsetFactor?: number,
        borderColor?: string,
        borderWidth?: number,
        borderRadius?: number,
        fontSize?: number,
        minFontSize?: number,
        fontFamily?: string,
        padding?: number,
        shadow?: boolean,
        shadowOptions?: { blur: number, offsetX: number, offsetY: number, color: string },
        glow?: boolean,
        glowOptions?: { color: string, blur: number },
        scaleFactor?: number,
    }): THREE.Sprite {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Multiline text desteği
        const lines = text.split('\n');
        const lineHeight = fontSize * 1.2; // Satır yüksekliği

        ctx.font = `${fontSize}px ${fontFamily}`;

        // En geniş satırı bul
        let maxWidth = 0;
        lines.forEach(line => {
            const lineWidth = ctx.measureText(line).width;
            if (lineWidth > maxWidth) maxWidth = lineWidth;
        });

        const width = maxWidth + padding * 2;
        const height = (lineHeight * lines.length) + padding * 2;

        canvas.width = width;
        canvas.height = height;

        ctx.font = `${fontSize}px ${fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        // Arka plan - Şeffaf hale getir
        if (backgroundColor || borderWidth > 0) {
            const r = borderRadius;
            ctx.beginPath();
            ctx.moveTo(r, 0);
            ctx.lineTo(width - r, 0);
            ctx.quadraticCurveTo(width, 0, width, r);
            ctx.lineTo(width, height - r);
            ctx.quadraticCurveTo(width, height, width - r, height);
            ctx.lineTo(r, height);
            ctx.quadraticCurveTo(0, height, 0, height - r);
            ctx.lineTo(0, r);
            ctx.quadraticCurveTo(0, 0, r, 0);
            ctx.closePath();

            if (backgroundColor) {
                // Rengi RGBA'ya çevir ve alfa değerini parametreden al
                const bgColor = this.hexToRgba(backgroundColor, backgroundAlpha);
                ctx.fillStyle = bgColor;
                ctx.fill();
            }

            if (borderWidth > 0) {
                ctx.lineWidth = borderWidth;
                ctx.strokeStyle = borderColor;
                ctx.stroke();
            }
        }

        // Gölge ve yazı
        if (shadow) {
            ctx.shadowColor = shadowOptions.color;
            ctx.shadowBlur = shadowOptions.blur;
            ctx.shadowOffsetX = shadowOptions.offsetX;
            ctx.shadowOffsetY = shadowOptions.offsetY;
        }

        if (glow) {
            ctx.shadowColor = glowOptions.color;
            ctx.shadowBlur = glowOptions.blur;
        }

        ctx.fillStyle = textColor;

        // Multiline text rendering
        lines.forEach((line, index) => {
            const y = padding + (index * lineHeight);
            ctx.fillText(line, width / 2, y);
        });

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const material = new THREE.SpriteMaterial({
            map: texture,
            depthTest: false,
            transparent: true,
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(width * scaleFactor, height * scaleFactor, 1);
        sprite.userData.isLabel = true;
        // ⚡ PERFORMANS: Canvas/texture referanslarini sakla (reuse icin)
        sprite.userData._labelCanvas = canvas;
        sprite.userData._labelTexture = texture;
        sprite.userData._labelScaleFactor = scaleFactor;
        return sprite;
    }

    /**
     * ⚡ PERFORMANS: Mevcut sprite'in canvas'ini yeniden ciz - sifir allocation
     * Yeni canvas/texture/material/sprite olusturmak yerine mevcut olanlari gunceller
     */
    private labelYenile(sprite: THREE.Sprite, {
        text,
        textColor = '#000000',
        backgroundColor,
        backgroundAlpha = 0,
        borderColor = '',
        borderWidth = 0,
        borderRadius = 0,
        fontSize = 11,
        fontFamily = 'Arial, Helvetica, sans-serif',
        padding = 2,
        shadow = false,
        shadowOptions = { blur: 0, offsetX: 0, offsetY: 0, color: 'transparent' },
        glow = false,
        glowOptions = { color: '#000', blur: 0 },
        scaleFactor = 0.035,
    }: {
        text: string,
        textColor?: string,
        backgroundColor?: string,
        backgroundAlpha?: number,
        borderColor?: string,
        borderWidth?: number,
        borderRadius?: number,
        fontSize?: number,
        fontFamily?: string,
        padding?: number,
        shadow?: boolean,
        shadowOptions?: { blur: number, offsetX: number, offsetY: number, color: string },
        glow?: boolean,
        glowOptions?: { color: string, blur: number },
        scaleFactor?: number,
    }): void {
        const canvas = sprite.userData._labelCanvas as HTMLCanvasElement;
        const texture = sprite.userData._labelTexture as THREE.CanvasTexture;
        if (!canvas || !texture) return;

        const ctx = canvas.getContext('2d')!;
        const lines = text.split('\n');
        const lineHeight = fontSize * 1.2;

        ctx.font = `${fontSize}px ${fontFamily}`;
        let maxWidth = 0;
        lines.forEach(line => {
            const w = ctx.measureText(line).width;
            if (w > maxWidth) maxWidth = w;
        });

        const width = maxWidth + padding * 2;
        const height = (lineHeight * lines.length) + padding * 2;

        // Canvas boyutunu guncelle (resize = auto clear)
        canvas.width = width;
        canvas.height = height;

        ctx.font = `${fontSize}px ${fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        if (backgroundColor || borderWidth > 0) {
            const r = borderRadius;
            ctx.beginPath();
            ctx.moveTo(r, 0);
            ctx.lineTo(width - r, 0);
            ctx.quadraticCurveTo(width, 0, width, r);
            ctx.lineTo(width, height - r);
            ctx.quadraticCurveTo(width, height, width - r, height);
            ctx.lineTo(r, height);
            ctx.quadraticCurveTo(0, height, 0, height - r);
            ctx.lineTo(0, r);
            ctx.quadraticCurveTo(0, 0, r, 0);
            ctx.closePath();

            if (backgroundColor) {
                ctx.fillStyle = this.hexToRgba(backgroundColor, backgroundAlpha);
                ctx.fill();
            }
            if (borderWidth > 0) {
                ctx.lineWidth = borderWidth;
                ctx.strokeStyle = borderColor;
                ctx.stroke();
            }
        }

        if (shadow) {
            ctx.shadowColor = shadowOptions.color;
            ctx.shadowBlur = shadowOptions.blur;
            ctx.shadowOffsetX = shadowOptions.offsetX;
            ctx.shadowOffsetY = shadowOptions.offsetY;
        }
        if (glow) {
            ctx.shadowColor = glowOptions.color;
            ctx.shadowBlur = glowOptions.blur;
        }

        ctx.fillStyle = textColor;
        lines.forEach((line, index) => {
            ctx.fillText(line, width / 2, padding + (index * lineHeight));
        });

        // ⚡ Mevcut texture'i guncelle - yeni texture olusturmadan
        texture.needsUpdate = true;

        // Sprite scale'ini guncelle
        sprite.scale.set(width * scaleFactor, height * scaleFactor, 1);
        sprite.userData._labelScaleFactor = scaleFactor;
    }

    /**
     * ⚡ Yazı pozisyonunu güncelle - yaziEkle ve reuse path icin ortak metot
     */
    private _yaziPozisyonGuncelle(konum?: THREE.Vector3, fontSize: number = 12): void {
        if (!this.yazi) return;

        if (konum) {
            this.yazi.position.copy(konum);
        } else if (this.olcumTuru === 'koordinat') {
            this.yazi.position.copy(this.nokta1).add(this.nokta2);
        } else if (this.olcumTuru === 'alan') {
            const ortaNokta = FikoOlcumCizgisi._tempOrtaNokta.addVectors(this.nokta1, this.nokta2).multiplyScalar(0.5);
            ortaNokta.x += this.offsetMesafe;
            this.yazi.position.copy(ortaNokta);
        } else {
            const dirVec = FikoOlcumCizgisi._tempDirVec.subVectors(this.nokta2, this.nokta1).normalize();
            let offsetDir = this.hesaplaOffsetYonu(dirVec);
            if (offsetDir.y < 0) offsetDir.negate();
            const offset1 = FikoOlcumCizgisi._tempOffset1.copy(this.nokta1).addScaledVector(offsetDir, this.offsetMesafe);
            const offset2 = FikoOlcumCizgisi._tempOffset2.copy(this.nokta2).addScaledVector(offsetDir, this.offsetMesafe);
            const ortaNokta = FikoOlcumCizgisi._tempOrtaNokta.addVectors(offset1, offset2).multiplyScalar(0.5);
            this.yazi.position.copy(ortaNokta);
        }

        if (this.olcumTuru !== 'koordinat' && this.olcumTuru !== 'alan') {
            const yaziYuksekligi = fontSize + 16;
            this.yazi.position.y += yaziYuksekligi * 0.5;
        }
    }

    /**
     * ✅ HEX rengi RGBA'ya çevir
     */
    private hexToRgba(hex: string, alpha: number = 1): string {
        // #RRGGBB formatını RGB'ye çevir
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    /**
     * ✅ Noktaları güncelle - BUGFIX: Local koordinatlara dönüştür + scale'i sabit tut
     */
    noktalarGuncelle(nokta1: THREE.Vector3, nokta2: THREE.Vector3, sahne?: FikoSahne): void {
        try {
            // Grup position'unu nokta1'e ayarla (world space'te doğru konumda)
            this.position.copy(nokta1);

            // Local koordinatlar: nokta2'yi nokta1'den başlayarak hesapla
            this.nokta1 = new THREE.Vector3(0, 0, 0); // Local origin
            this.nokta2 = new THREE.Vector3().subVectors(nokta2, nokta1); // nokta2 - nokta1
            this.uzunluk = this.nokta1.distanceTo(this.nokta2);

            // Çizgi ve okları yeniden oluştur
            if (this.isArc) {
                this.arcOlustur();
            } else {
                this.cizgiOlustur();
            }
            this.okOlustur();

            // Yazıyı yeniden hazırla (varsa)
            if (this.yazi) {
                const ortaNokta = new THREE.Vector3().addVectors(this.nokta1, this.nokta2).multiplyScalar(0.5);
                this.yazi.position.copy(ortaNokta);

                // 🔴 BUGFIX: Yazıyı yukarı kaydır (fontSize kadar)
                // Default fontSize = 24, padding = 8*2 = 16
                const yaziYuksekligi = 24 + 16;
                this.yazi.position.y += yaziYuksekligi * 0.5;
            }

            // Zoom listener'ını kontrol et (ilk kez çağrılmışsa)
            if (sahne && !this.hazirlaListenerEklendi) {
                this.hazirla(sahne);
            }

            // Matrix güncelle
            this.updateMatrixWorld(true);

        } catch (e) {
        }
    }

    /**
     * ✅ Renkler güncelle
     */
    renkleriGuncelle(renkler: { cizgi?: number, ok?: number, yazi?: string }): void {
        if (renkler.cizgi) this.renkler.cizgi = renkler.cizgi;
        if (renkler.ok) this.renkler.ok = renkler.ok;
        if (renkler.yazi) this.renkler.yazi = renkler.yazi;

        this.cizgiOlustur();
        this.okOlustur();
    }

    /**
     * ✅ Sahneye hazırlama
     * 🚀 OPTIMIZE: Lazy initialization - ilk scale sadece yazı varsa hesaplanır
     */
    override hazirla(sahne?: FikoSahne) {
        super.hazirla(sahne);

        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) {
            return;
        }

        try {
            // 🚀 OPTIMIZE: İlk scale'i sadece yazı varsa ayarla (gereksiz çağrı önlenir)
            if (this.yazi && this.yazi.userData.originalScale) {
                this.kameraYonDegisti(this);
            }

            // 🔴 Kamera değiştiğinde - EVENT LİSTENER (Fiko3DYazi pattern'i)
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }

        } catch (e) {
        }
    }

    /**
     * 🚀 PERFORMANS: Throttled kamera event handler
     * Her frame'de değil, 100ms'de bir güncelle (zoom sırasında donmayı önler)
     * ⚡ ULTRA FIX: RAF queue overflow yerine setTimeout kullan (RAF biriktirme yok)
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        // 🚀 FIX: RAF queue dolmasını önle - setTimeout ile simple throttle
        if (this.kameraUpdateTimeout !== null) {
            // Zaten bekleyen bir update var, skiple
            return;
        }

        // Throttle: Son güncellemeden beri yeterli süre geçti mi?
        if (now - this.lastKameraUpdateTime < FikoOlcumCizgisi.KAMERA_THROTTLE_MS) {
            // setTimeout ile throttle (RAF queue overflow yok!)
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti(this);
            }, FikoOlcumCizgisi.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        // Hemen çalıştır
        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti(this);
    }

    /**
     * 🔴 YAZININ BOYUTU SABİT - Çizgi normal kalır
     * Parent scale'i etkilemiyoruz, sadece yazı sprite'ının scale'ini kontrol ediyoruz
     * 🚀 OPTIMIZE: updateMatrixWorld kaldırıldı (gereksiz tam scene traverse)
     * ⚡ ULTRA FIX: updateMatrix() kaldırıldı - Three.js otomatik render loop'ta halleder (matrixAutoUpdate: true)
     * 🎯 SMOOTH FIX: Hedef scale hesaplama (interpolation loop RAF'ta çalışır)
     */
    kameraYonDegisti(ev: any, immediate: boolean = false) {
        if (!this.sahne || !this.sahne.anaGosterim || !this.sahne.anaGosterim.aktifKamera) {
            return;
        }

        // 🔴 SADECE YAZININ BOYUTU SABİT KALSYN
        // Parent group'u scale etmiyoruz - çizgi, oklar, extension line'lar normal kalır
        if (this.sabitBoyut && this.yazi && this.yazi.userData.originalScale) {
            const kamera = this.sahne.anaGosterim.aktifKamera;
            const zoom = kamera?.zoom ?? 1;
            const factor = (kamera?.top - kamera?.bottom) / zoom;

            // 🎯 SMOOTH: Sadece hedef scale'i güncelle (asıl interpolation RAF'ta)
            this.targetScaleAdjustment = factor * 0.02;

            // 🎯 v3.3.1 FIX: Her zaman hemen scale uygula (interpolation çift görüntü sorununa neden oluyordu)
            // NOT: Smooth interpolation geçici olarak devre dışı - çift label sorunu araştırılıyor
            this.currentScaleAdjustment = this.targetScaleAdjustment;
            const originalScale = this.yazi.userData.originalScale as THREE.Vector3;
            this.yazi.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );
            return;

            // 🎯 v2.1.0: Sürükleme sırasında hemen scale uygula (interpolation yok)
            // DEVRE DIŞI - v3.3.1 değişikliği ile yukarıda her zaman hemen uygulama yapılıyor
            /*
            if (immediate) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                const originalScale = this.yazi.userData.originalScale as THREE.Vector3;
                this.yazi.scale.set(
                    originalScale.x * this.currentScaleAdjustment,
                    originalScale.y * this.currentScaleAdjustment,
                    1
                );
                return;
            }

            // 🎯 SMOOTH: Interpolation loop başlat (eğer çalışmıyorsa)
            this.startSmoothInterpolation();
            */
        }
    }

    /**
     * 🎯 SMOOTH: Her frame interpolation (takılmadan smooth scaling)
     */
    private startSmoothInterpolation(): void {
        // Zaten çalışıyorsa, tekrar başlatma
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.yazi || !this.yazi.userData.originalScale || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            // 🎯 SMOOTH: Linear interpolation (lerp)
            const lerpFactor = 0.2; // 0.2 = responsive ve smooth denge
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            // Convergence check - hedefe çok yakınsa dur (gereksiz RAF)
            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            // Interpolate
            this.currentScaleAdjustment += diff * lerpFactor;

            // Scale uygula
            const originalScale = this.yazi.userData.originalScale as THREE.Vector3;
            this.yazi.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            // Next frame
            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        // İlk frame'i başlat
        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * ✅ JSON'a dönüştür
     */
    override toJSON(meta?: { geometries: any; materials: any; textures: any; images: any }): any {
        return {
            type: this.type,
            isim: this.name,
            nokta1: this.nokta1,
            nokta2: this.nokta2,
            uzunluk: this.uzunluk,
            renkler: this.renkler,
            matrix: this.matrix.toArray()
        };
    }

    /**
     * ✅ JSON'dan oluştur
     */
    static fromJson(sahne?: FikoSahne, parent?: any, json?: any): FikoOlcumCizgisi {
        const nokta1 = new THREE.Vector3().fromArray(json.nokta1);
        const nokta2 = new THREE.Vector3().fromArray(json.nokta2);

        const olcum = new FikoOlcumCizgisi(
            sahne,
            json.isim,
            nokta1,
            nokta2,
            json.uzunluk,
            json.renkler
        );

        olcum.parent = parent;
        return olcum;
    }

    /**
     * ✅ PERFORMANS: Kaynakları serbest bırak (CATIA seviyesi stabilite)
     * Memory leak önleme, hızlı temizlik
     */
    dispose(): void {
        if (this.isDisposed) return; // Double dispose önleme
        this.isDisposed = true;

        // v2.0.0 - DimensionSettings entegrasyonu: Instance'ı kaldır
        FikoOlcumCizgisi._instances.delete(this);

        // 🚀 Timeout temizle (memory leak önleme) - RAF → setTimeout oldu
        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        // 🎯 SMOOTH: Interpolation RAF temizle
        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        // Event listener temizle
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        // ✅ Tüm elemanları temizle (yeni metodlardan gelenler dahil)
        this.temizleElemanlar();

        // Çizgi - geometry ve material pool'a geri ver (reuse için)
        if (this.cizgi) {
            this.remove(this.cizgi);
            // Material pool'a geri koy (reuse)
            if (!FikoOlcumCizgisi.materialPool.has('dimension')) {
                FikoOlcumCizgisi.materialPool.set('dimension', this.cizgi.material as LineMaterial);
            } else {
                (this.cizgi.material as LineMaterial).dispose();
            }
            (this.cizgi.geometry as LineGeometry).dispose();
            this.cizgi = null;
        }

        // Extension lines - hızlı temizlik
        if (this.extensionLine1) {
            this.remove(this.extensionLine1);
            (this.extensionLine1.geometry as LineGeometry).dispose();
            (this.extensionLine1.material as LineMaterial).dispose();
            this.extensionLine1 = null;
        }

        if (this.extensionLine2) {
            this.remove(this.extensionLine2);
            (this.extensionLine2.geometry as LineGeometry).dispose();
            (this.extensionLine2.material as LineMaterial).dispose();
            this.extensionLine2 = null;
        }

        // Oklar - 🚀 geometry shared olduğu için sadece material dispose et
        if (this.ok1) {
            this.remove(this.ok1);
            // Geometry shared - dispose etme!
            ((this.ok1 as THREE.Mesh).material as THREE.Material).dispose();
            this.ok1 = null;
        }

        if (this.ok2) {
            this.remove(this.ok2);
            // Geometry shared - dispose etme!
            ((this.ok2 as THREE.Mesh).material as THREE.Material).dispose();
            this.ok2 = null;
        }

        // Yazı - texture ve material temizlik
        if (this.yazi) {
            this.remove(this.yazi);
            const texture = (this.yazi.material as THREE.SpriteMaterial).map;
            if (texture) texture.dispose();
            ((this.yazi as THREE.Sprite).material as THREE.Material).dispose();
            this.yazi = null;
        }

        // ✅ Arc çizgisi temizle
        if (this.arcCizgi) {
            this.remove(this.arcCizgi);
            (this.arcCizgi.geometry as LineGeometry)?.dispose();
            (this.arcCizgi.material as LineMaterial)?.dispose();
            this.arcCizgi = null;
        }

        // ✅ Radius çizgisi temizle
        if (this.radiusCizgi) {
            this.remove(this.radiusCizgi);
            (this.radiusCizgi.geometry as LineGeometry)?.dispose();
            (this.radiusCizgi.material as LineMaterial)?.dispose();
            this.radiusCizgi = null;
        }

        // ✅ Merkez işareti temizle
        if (this.merkezIsareti) {
            this.remove(this.merkezIsareti);
            this.merkezIsareti.traverse((child) => {
                if ((child as THREE.Mesh).geometry) {
                    (child as THREE.Mesh).geometry.dispose();
                }
                if ((child as THREE.Mesh).material) {
                    ((child as THREE.Mesh).material as THREE.Material).dispose();
                }
            });
            this.merkezIsareti = null;
        }

        // ✅ Koordinat crosshair temizle
        if (this.koordinatCrosshair) {
            this.remove(this.koordinatCrosshair);
            this.koordinatCrosshair.traverse((child) => {
                if ((child as THREE.Mesh).geometry) {
                    (child as THREE.Mesh).geometry.dispose();
                }
                if ((child as THREE.Mesh).material) {
                    ((child as THREE.Mesh).material as THREE.Material).dispose();
                }
            });
            this.koordinatCrosshair = null;
        }

        // Parent dispose çağır
        // super.dispose?.();
    }

    /**
     * ✅ PERFORMANS: Static pool cleanup (uygulama kapanırken)
     * 🚀 GPU-READY: Tüm shared resource'ları temizle
     */
    static clearPools(): void {
        FikoOlcumCizgisi.materialPool.forEach(mat => mat.dispose());
        FikoOlcumCizgisi.materialPool.clear();
        FikoOlcumCizgisi.geometryPool.forEach(geom => geom.dispose());
        FikoOlcumCizgisi.geometryPool.clear();

        // 🚀 Shared cone geometry'yi de temizle
        if (FikoOlcumCizgisi._sharedConeGeometry) {
            FikoOlcumCizgisi._sharedConeGeometry.dispose();
            FikoOlcumCizgisi._sharedConeGeometry = null;
        }
        if (FikoOlcumCizgisi._sharedConeMaterial) {
            FikoOlcumCizgisi._sharedConeMaterial.dispose();
            FikoOlcumCizgisi._sharedConeMaterial = null;
        }
    }

    // =====================================================================
    // ✅ CATIA TARZI ÖZEL ÖLÇÜM TÜRLERİ - PROFESYONEL GÖSTERİM
    // =====================================================================

    /**
     * ✅ YARICAP ÇİZGİSİ OLUŞTUR - CATIA/IRONCAD PROFESYONEL
     * 🎯 INDUSTRIAL STANDARD: Leader line + R sembolü + merkez işareti
     * Yarıçap değeri kıvrımlı leader line ile gösterilir
     */
    private yaricapCizgiOlustur(): void {
        try {
            // Eski elemanları temizle
            this.temizleElemanlar();

            // Yarıçap hesapla
            const yariCap = this.nokta1.distanceTo(this.nokta2);

            // 🎯 CATIA TARZI: Büyük profesyonel ok boyutları
            const okRadius = Math.max(0.15, Math.min(0.4, yariCap * 0.035));
            const okHeight = Math.max(0.4, Math.min(1.0, yariCap * 0.08));

            // Merkez işareti oluştur (nokta1 = merkez)
            this.merkezIsaretiOlustur();

            // 🎯 LEADER LINE: Merkez'den yay üzerine, sonra kırılıp yazıya
            // İki segmentli profesyonel leader
            const dirVec = new THREE.Vector3().subVectors(this.nokta2, this.nokta1).normalize();

            // Leader kırılma noktası (yay üzerinden %30 dışarı)
            const leaderKirilma = this.nokta2.clone().addScaledVector(dirVec, yariCap * 0.15);

            // Leader yatay segment (yazıya doğru)
            const leaderBitis = leaderKirilma.clone();
            leaderBitis.x += yariCap * 0.25; // Sağa doğru yatay

            // Yarıçap çizgisi - Merkez'den yay üzerine
            const radiusGeometry = new LineGeometry();
            radiusGeometry.setPositions([
                this.nokta1.x, this.nokta1.y, this.nokta1.z, // Merkez
                this.nokta2.x, this.nokta2.y, this.nokta2.z  // Yay üzerindeki nokta
            ]);

            const radiusMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi);

            this.radiusCizgi = new LineSegments2(radiusGeometry, radiusMaterial);
            this.radiusCizgi.name = 'RadiusLine';
            this.radiusCizgi.renderOrder = Infinity;
            this.radiusCizgi.computeLineDistances();
            this.radiusCizgi.frustumCulled = false;
            this.add(this.radiusCizgi);

            // 🎯 LEADER LINE: Yay ucundan yazıya kırılmalı çizgi
            const leaderGeometry = new LineGeometry();
            leaderGeometry.setPositions([
                this.nokta2.x, this.nokta2.y, this.nokta2.z,     // Yay üzerinde
                leaderKirilma.x, leaderKirilma.y, leaderKirilma.z, // Kırılma noktası
                leaderBitis.x, leaderBitis.y, leaderBitis.z       // Yazı yanı
            ]);

            const leaderMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi * 0.7);

            const leaderLine = new Line2(leaderGeometry, leaderMaterial);
            leaderLine.name = 'RadiusLeader';
            leaderLine.renderOrder = Infinity;
            leaderLine.computeLineDistances();
            leaderLine.frustumCulled = false;
            this.add(leaderLine);

            // 🎯 Ok yay ucunda, merkeze doğru bakıyor
            const ok = this.okOlusturHelper(this.nokta2.clone(), dirVec, okRadius, okHeight);
            ok.name = 'RadiusArrow';
            this.ok1 = ok;
            this.add(ok);

        } catch (e) {
            // yaricapCizgiOlustur error - silently handle
        }
    }

    /**
     * ✅ ÇAP ÇİZGİSİ OLUŞTUR - CATIA/IRONCAD PROFESYONEL
     * 🎯 INDUSTRIAL STANDARD: Merkez geçen çap çizgisi + Ø sembolü + merkez işareti + iki uçta ok
     * Profesyonel CAD ölçüm gösterimi - bi-directional arrowheads
     */
    private capCizgiOlustur(): void {
        try {
            // Eski elemanları temizle
            this.temizleElemanlar();

            // ✅ KRITIK: nokta1 ve nokta2 zaten çapın iki ucu (çemberin karşılıklı noktaları)
            const capUc1 = this.nokta1.clone();
            const capUc2 = this.nokta2.clone();

            // Merkez: iki ucun ortası
            const merkez = new THREE.Vector3().addVectors(capUc1, capUc2).multiplyScalar(0.5);

            // Çap ve yarıçap
            const cap = capUc1.distanceTo(capUc2);
            const yariCap = cap / 2;

            // 🎯 CATIA TARZI: Büyük profesyonel ok boyutları
            const okRadius = Math.max(0.15, Math.min(0.4, cap * 0.02));
            const okHeight = Math.max(0.4, Math.min(1.0, cap * 0.05));

            // Merkez işareti oluştur - Geçici olarak nokta1'i merkez olarak ayarla
            const originalNokta1 = this.nokta1.clone();
            this.nokta1.copy(merkez);
            this.merkezIsaretiOlustur();
            this.nokta1.copy(originalNokta1);

            // 🎯 ÇAP ÇİZGİSİ: İki uçta solid filled oklar ile profesyonel görünüm
            const capGeometry = new LineGeometry();
            capGeometry.setPositions([
                capUc1.x, capUc1.y, capUc1.z,
                capUc2.x, capUc2.y, capUc2.z
            ]);

            const capMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi);

            this.cizgi = new LineSegments2(capGeometry, capMaterial);
            this.cizgi.name = 'DiameterLine';
            this.cizgi.renderOrder = Infinity;
            this.cizgi.computeLineDistances();
            this.cizgi.frustumCulled = false;
            this.add(this.cizgi);

            // 🎯 BI-DIRECTIONAL ARROWHEADS: Oklar merkeze doğru (içeri) bakıyor
            // CAD standardı: Çap ölçümünde oklar içeri, mesafe ölçümünde oklar dışarı
            const capYonu = new THREE.Vector3().subVectors(capUc2, capUc1).normalize();

            // Ok1: capUc1'de, merkeze doğru bakıyor (capUc2 yönüne)
            const ok1Yonu = capYonu.clone().multiplyScalar(-1);
            this.ok1 = this.okOlusturHelper(capUc1, ok1Yonu, okRadius, okHeight);
            this.ok1.name = 'DiameterArrow1';
            this.add(this.ok1);

            // Ok2: capUc2'de, merkeze doğru bakıyor (capUc1 yönüne)
            const ok2Yonu = capYonu.clone();
            this.ok2 = this.okOlusturHelper(capUc2, ok2Yonu, okRadius, okHeight);
            this.ok2.name = 'DiameterArrow2';
            this.add(this.ok2);

            // 🎯 LEADER LINE: Çap yazısı için kırılmalı leader (opsiyon)
            // Yazı merkeze yakın konumlandırılır, leader ile bağlanır
            const leaderBaslangic = merkez.clone();
            leaderBaslangic.y += yariCap * 0.3; // Yukarı

            const leaderBitis = leaderBaslangic.clone();
            leaderBitis.x += cap * 0.15; // Sağa yatay

            const leaderGeometry = new LineGeometry();
            leaderGeometry.setPositions([
                merkez.x, merkez.y, merkez.z,
                leaderBaslangic.x, leaderBaslangic.y, leaderBaslangic.z,
                leaderBitis.x, leaderBitis.y, leaderBitis.z
            ]);

            const leaderMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi * 0.6, 0.7);

            const leaderLine = new Line2(leaderGeometry, leaderMaterial);
            leaderLine.name = 'DiameterLeader';
            leaderLine.renderOrder = Infinity;
            leaderLine.computeLineDistances();
            leaderLine.frustumCulled = false;
            this.add(leaderLine);

        } catch (e) {
            // capCizgiOlustur error - silently handle
        }
    }

    /**
     * ✅ ACI ARC OLUŞTUR - CATIA/IRONCAD PROFESYONEL
     * 🎯 INDUSTRIAL STANDARD: Yay çizgisi + açı yazısı (°) + iki uçta ok
     * Profesyonel açı ölçümü gösterimi
     */
    private aciArcOlustur(): void {
        try {
            // Eski elemanları temizle
            this.temizleElemanlar();

            // Arc parametreleri
            const merkez = this.merkezNoktası.lengthSq() > 0 ? this.merkezNoktası : this.nokta1;
            const mesafe = merkez.distanceTo(this.nokta2);
            const yariCap = mesafe * 0.5; // Arc yarıçapı (kompakt profesyonel görünüm)

            // Başlangıç ve bitiş açılarını hesapla
            const baslangicVec = new THREE.Vector3().subVectors(this.nokta1, merkez).normalize();
            const bitisVec = new THREE.Vector3().subVectors(this.nokta2, merkez).normalize();

            // 🎯 PROFESYONEL: Daha fazla segment = pürüzsüz arc
            const segments = Math.max(48, Math.ceil(Math.abs(this.acı) / 2));
            const arcPoints: THREE.Vector3[] = [];

            for (let i = 0; i <= segments; i++) {
                const t = i / segments;
                // Slerp interpolasyon kullanarak yay çiz
                const slerpedDir = new THREE.Vector3().copy(baslangicVec).lerp(bitisVec, t).normalize();
                const arcPoint = merkez.clone().add(slerpedDir.multiplyScalar(yariCap));
                arcPoints.push(arcPoint);
            }

            // Arc geometry oluştur
            const arcGeometry = new LineGeometry();
            const positions: number[] = [];
            arcPoints.forEach(p => {
                positions.push(p.x, p.y, p.z);
            });
            arcGeometry.setPositions(positions);

            const arcMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi);

            this.arcCizgi = new Line2(arcGeometry, arcMaterial);
            this.arcCizgi.name = 'AngleArc';
            this.arcCizgi.renderOrder = Infinity;
            this.arcCizgi.computeLineDistances();
            this.arcCizgi.frustumCulled = false;
            this.add(this.arcCizgi);

            // 🎯 PROFESYONEL OK BOYUTLARI (mesafeye orantılı)
            // 🎯 CATIA TARZI: Büyük profesyonel ok boyutları
            const okRadius = Math.max(0.12, Math.min(0.35, yariCap * 0.045));
            const okHeight = Math.max(0.35, Math.min(0.9, yariCap * 0.1));

            // Arc uçlarında oklar
            if (arcPoints.length >= 2) {
                const ilkNokta = arcPoints[0];
                const ikinciNokta = arcPoints[1];
                const sonNokta = arcPoints[arcPoints.length - 1];
                const sonOncesiNokta = arcPoints[arcPoints.length - 2];

                // İlk ok - arc başlangıcında (teğet yönü)
                const ok1Yonu = new THREE.Vector3().subVectors(ikinciNokta, ilkNokta).normalize();
                this.ok1 = this.okOlusturHelper(ilkNokta, ok1Yonu.clone().multiplyScalar(-1), okRadius, okHeight);
                this.ok1.name = 'ArcArrow1';
                this.add(this.ok1);

                // Son ok - arc sonunda (teğet yönü)
                const ok2Yonu = new THREE.Vector3().subVectors(sonNokta, sonOncesiNokta).normalize();
                this.ok2 = this.okOlusturHelper(sonNokta, ok2Yonu, okRadius, okHeight);
                this.ok2.name = 'ArcArrow2';
                this.add(this.ok2);
            }

            // Merkez işareti (küçük nokta)
            this.merkezIsaretiOlustur();

            // 🎯 PROFESYONEL: İnce kesikli extension lines
            this.extensionLinesOlusturAci(merkez, this.nokta1, this.nokta2);

        } catch (e) {
            // aciArcOlustur error - silently handle
        }
    }

    /**
     * ✅ KOORDİNAT CROSSHAIR OLUŞTUR - CATIA TARZI
     * X-Y-Z eksenleri gösteren crosshair işareti + leader line
     * 🎯 v2.1.0: Leader line ve sürükleme desteği eklendi
     */
    private koordinatCrosshairOlustur(): void {
        try {
            // Eski elemanları temizle
            this.temizleElemanlar();

            const crosshairSize = 3; // Crosshair boyutu (küçültüldü)

            // Crosshair grubu
            this.koordinatCrosshair = new THREE.Group();
            this.koordinatCrosshair.name = 'KoordinatCrosshair';

            // Merkez noktası (küçük küre) - Önce ekle
            const merkezGeom = new THREE.SphereGeometry(1, 16, 16);
            const merkezMat = FikoOlcumCizgisi.getPooledArrowMaterial(this.renkler.cizgi);
            const merkezKure = new THREE.Mesh(merkezGeom, merkezMat);
            merkezKure.position.copy(this.nokta1);
            merkezKure.renderOrder = Infinity;
            merkezKure.frustumCulled = false;
            this.koordinatCrosshair.add(merkezKure);

            // X ekseni (kırmızı)
            const xGeometry = new LineGeometry();
            xGeometry.setPositions([
                this.nokta1.x - crosshairSize, this.nokta1.y, this.nokta1.z,
                this.nokta1.x + crosshairSize, this.nokta1.y, this.nokta1.z
            ]);
            const xMaterial = FikoOlcumCizgisi.getPooledLineMaterial(0xFF4444, this.cizgiKalinligi * 0.8);
            const xLine = new LineSegments2(xGeometry, xMaterial);
            xLine.name = 'CrosshairX';
            xLine.renderOrder = Infinity;
            xLine.frustumCulled = false;
            this.koordinatCrosshair.add(xLine);

            // Y ekseni (yeşil)
            const yGeometry = new LineGeometry();
            yGeometry.setPositions([
                this.nokta1.x, this.nokta1.y - crosshairSize, this.nokta1.z,
                this.nokta1.x, this.nokta1.y + crosshairSize, this.nokta1.z
            ]);
            const yMaterial = FikoOlcumCizgisi.getPooledLineMaterial(0x44FF44, this.cizgiKalinligi * 0.8);
            const yLine = new LineSegments2(yGeometry, yMaterial);
            yLine.name = 'CrosshairY';
            yLine.renderOrder = Infinity;
            yLine.frustumCulled = false;
            this.koordinatCrosshair.add(yLine);

            // Z ekseni (mavi)
            const zGeometry = new LineGeometry();
            zGeometry.setPositions([
                this.nokta1.x, this.nokta1.y, this.nokta1.z - crosshairSize,
                this.nokta1.x, this.nokta1.y, this.nokta1.z + crosshairSize
            ]);
            const zMaterial = FikoOlcumCizgisi.getPooledLineMaterial(0x4444FF, this.cizgiKalinligi * 0.8);
            const zLine = new LineSegments2(zGeometry, zMaterial);
            zLine.name = 'CrosshairZ';
            zLine.renderOrder = Infinity;
            zLine.frustumCulled = false;
            this.koordinatCrosshair.add(zLine);

            // 🎯 v3.3.6: Leader line - noktadan yazıya uzanan çizgi
            // nokta2 sadece ilk kez ayarlanır, sonraki çağrılarda mevcut değer korunur (sürükleme için)
            if (this.nokta2.x === 0 && this.nokta2.y === 0 && this.nokta2.z === 0) {
                const leaderOffset = this.offsetMesafe || 15;
                this.nokta2.set(leaderOffset, leaderOffset * 0.5, 0);
            }

            // Leader line ucu nokta2'den hesapla (nokta1'e göre göreceli)
            const leaderEndX = this.nokta1.x + this.nokta2.x;
            const leaderEndY = this.nokta1.y + this.nokta2.y;
            const leaderEndZ = this.nokta1.z + this.nokta2.z;

            const leaderGeometry = new LineGeometry();
            leaderGeometry.setPositions([
                this.nokta1.x, this.nokta1.y, this.nokta1.z,
                leaderEndX, leaderEndY, leaderEndZ
            ]);
            const leaderMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi);
            const leaderLine = new LineSegments2(leaderGeometry, leaderMaterial);
            leaderLine.name = 'LeaderLine';
            leaderLine.renderOrder = Infinity;
            leaderLine.frustumCulled = false;
            this.koordinatCrosshair.add(leaderLine);

            this.koordinatCrosshair.renderOrder = Infinity;
            this.koordinatCrosshair.frustumCulled = false;
            this.add(this.koordinatCrosshair);

        } catch (e) {
            // koordinatCrosshairOlustur error - silently handle
        }
    }

    /**
     * ✅ ALAN İŞARETİ OLUŞTUR - CATIA TARZI
     * Yüzey merkez noktası gösterimi
     */
    private alanIsaretiOlustur(): void {
        try {
            // Eski elemanları temizle
            this.temizleElemanlar();

            // Merkez işareti - dörtlü daire pattern
            this.merkezIsareti = new THREE.Group();
            this.merkezIsareti.name = 'AlanMerkezIsareti';

            const isaretiSize = 3;

            // Dış daire (çember)
            const circleSegments = 32;
            const circlePositions: number[] = [];
            for (let i = 0; i <= circleSegments; i++) {
                const theta = (i / circleSegments) * Math.PI * 2;
                circlePositions.push(
                    this.nokta1.x + Math.cos(theta) * isaretiSize,
                    this.nokta1.y + Math.sin(theta) * isaretiSize,
                    this.nokta1.z
                );
            }

            const circleGeometry = new LineGeometry();
            circleGeometry.setPositions(circlePositions);
            const circleMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.cizgi, this.cizgiKalinligi * 0.7);
            const circleLine = new Line2(circleGeometry, circleMaterial);
            circleLine.name = 'AlanCircle';
            circleLine.renderOrder = Infinity;
            circleLine.frustumCulled = false;
            this.merkezIsareti.add(circleLine);

            // Çapraz çizgiler (X pattern)
            const crossGeometry = new LineGeometry();
            crossGeometry.setPositions([
                // Çizgi 1: sol-alt -> sağ-üst
                this.nokta1.x - isaretiSize * 0.7, this.nokta1.y - isaretiSize * 0.7, this.nokta1.z,
                this.nokta1.x + isaretiSize * 0.7, this.nokta1.y + isaretiSize * 0.7, this.nokta1.z,
                // Çizgi 2: sol-üst -> sağ-alt
                this.nokta1.x - isaretiSize * 0.7, this.nokta1.y + isaretiSize * 0.7, this.nokta1.z,
                this.nokta1.x + isaretiSize * 0.7, this.nokta1.y - isaretiSize * 0.7, this.nokta1.z
            ]);
            const crossMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.centerMark, this.cizgiKalinligi * 0.5);
            const crossLine = new LineSegments2(crossGeometry, crossMaterial);
            crossLine.name = 'AlanCross';
            crossLine.renderOrder = Infinity;
            crossLine.frustumCulled = false;
            this.merkezIsareti.add(crossLine);

            this.merkezIsareti.renderOrder = Infinity;
            this.merkezIsareti.frustumCulled = false;
            this.add(this.merkezIsareti);

        } catch (e) {
            // alanIsaretiOlustur error - silently handle
        }
    }

    /**
     * ✅ MERKEZ İŞARETİ OLUŞTUR - CATIA/IRONCAD PROFESYONEL
     * 🎯 INDUSTRIAL STANDARD: Kompakt + şeklinde crosshair işareti
     * Profesyonel CAD merkez gösterimi
     */
    private merkezIsaretiOlustur(): void {
        // 🎯 PROFESYONEL: Kompakt merkez işareti (yarıçapa göre ölçeklenir)
        const yariCap = this.nokta1.distanceTo(this.nokta2);
        const crosshairSize = Math.max(0.8, Math.min(2.5, yariCap * 0.08)); // Kompakt boyut

        this.merkezIsareti = new THREE.Group();
        this.merkezIsareti.name = 'MerkezIsareti';

        // 🎯 Yatay çizgi - ince ve profesyonel
        const hGeometry = new LineGeometry();
        hGeometry.setPositions([
            this.nokta1.x - crosshairSize, this.nokta1.y, this.nokta1.z,
            this.nokta1.x + crosshairSize, this.nokta1.y, this.nokta1.z
        ]);
        const hMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.centerMark, this.cizgiKalinligi * 0.5);
        const hLine = new LineSegments2(hGeometry, hMaterial);
        hLine.name = 'CenterHorizontal';
        hLine.renderOrder = Infinity;
        hLine.frustumCulled = false;
        this.merkezIsareti.add(hLine);

        // 🎯 Dikey çizgi - ince ve profesyonel
        const vGeometry = new LineGeometry();
        vGeometry.setPositions([
            this.nokta1.x, this.nokta1.y - crosshairSize, this.nokta1.z,
            this.nokta1.x, this.nokta1.y + crosshairSize, this.nokta1.z
        ]);
        const vMaterial = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.centerMark, this.cizgiKalinligi * 0.5);
        const vLine = new LineSegments2(vGeometry, vMaterial);
        vLine.name = 'CenterVertical';
        vLine.renderOrder = Infinity;
        vLine.frustumCulled = false;
        this.merkezIsareti.add(vLine);

        // 🎯 Merkez noktası - solid filled küçük daire
        const dotSize = Math.max(0.15, crosshairSize * 0.15); // Kompakt nokta
        const dotGeometry = new THREE.CircleGeometry(dotSize, 12);
        const dotMaterial = FikoOlcumCizgisi.getPooledArrowMaterial(this.renkler.centerMark);
        const dot = new THREE.Mesh(dotGeometry, dotMaterial);
        dot.position.copy(this.nokta1);
        dot.renderOrder = Infinity;
        dot.frustumCulled = false;
        this.merkezIsareti.add(dot);

        this.merkezIsareti.renderOrder = Infinity;
        this.merkezIsareti.frustumCulled = false;
        this.add(this.merkezIsareti);
    }

    /**
     * ✅ ACI İÇİN EXTENSION LINES - Merkez'den başlangıç ve bitiş noktalarına
     */
    private extensionLinesOlusturAci(merkez: THREE.Vector3, baslangic: THREE.Vector3, bitis: THREE.Vector3): void {
        // Extension line 1: Merkez -> Başlangıç
        const ext1Geometry = new LineGeometry();
        ext1Geometry.setPositions([
            merkez.x, merkez.y, merkez.z,
            baslangic.x, baslangic.y, baslangic.z
        ]);
        const ext1Material = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.extensionLine, this.cizgiKalinligi * 0.4, 1.0, true);
        this.extensionLine1 = new LineSegments2(ext1Geometry, ext1Material);
        this.extensionLine1.name = 'AciExtension1';
        this.extensionLine1.renderOrder = Infinity;
        this.extensionLine1.computeLineDistances();
        this.extensionLine1.frustumCulled = false;
        this.add(this.extensionLine1);

        // Extension line 2: Merkez -> Bitiş
        const ext2Geometry = new LineGeometry();
        ext2Geometry.setPositions([
            merkez.x, merkez.y, merkez.z,
            bitis.x, bitis.y, bitis.z
        ]);
        const ext2Material = FikoOlcumCizgisi.getPooledLineMaterial(this.renkler.extensionLine, this.cizgiKalinligi * 0.4, 1.0, true);
        this.extensionLine2 = new LineSegments2(ext2Geometry, ext2Material);
        this.extensionLine2.name = 'AciExtension2';
        this.extensionLine2.renderOrder = Infinity;
        this.extensionLine2.computeLineDistances();
        this.extensionLine2.frustumCulled = false;
        this.add(this.extensionLine2);
    }

    /**
     * ✅ ELEMANLARI TEMİZLE - Yeni çizim öncesi
     */
    private temizleElemanlar(): void {
        // Ana çizgi
        if (this.cizgi) {
            this.remove(this.cizgi);
            (this.cizgi.geometry as LineGeometry)?.dispose();
            (this.cizgi.material as LineMaterial)?.dispose();
            this.cizgi = null;
        }

        // Extension lines
        if (this.extensionLine1) {
            this.remove(this.extensionLine1);
            (this.extensionLine1.geometry as LineGeometry)?.dispose();
            (this.extensionLine1.material as LineMaterial)?.dispose();
            this.extensionLine1 = null;
        }
        if (this.extensionLine2) {
            this.remove(this.extensionLine2);
            (this.extensionLine2.geometry as LineGeometry)?.dispose();
            (this.extensionLine2.material as LineMaterial)?.dispose();
            this.extensionLine2 = null;
        }

        // Oklar
        if (this.ok1) {
            this.remove(this.ok1);
            this.ok1.geometry?.dispose();
            (this.ok1.material as THREE.Material)?.dispose();
            this.ok1 = null;
        }
        if (this.ok2) {
            this.remove(this.ok2);
            this.ok2.geometry?.dispose();
            (this.ok2.material as THREE.Material)?.dispose();
            this.ok2 = null;
        }

        // Merkez işareti
        if (this.merkezIsareti) {
            this.remove(this.merkezIsareti);
            this.merkezIsareti.traverse((child) => {
                if ((child as THREE.Mesh).geometry) {
                    (child as THREE.Mesh).geometry.dispose();
                }
                if ((child as THREE.Mesh).material) {
                    ((child as THREE.Mesh).material as THREE.Material).dispose();
                }
            });
            this.merkezIsareti = null;
        }

        // Arc çizgisi
        if (this.arcCizgi) {
            this.remove(this.arcCizgi);
            (this.arcCizgi.geometry as LineGeometry)?.dispose();
            (this.arcCizgi.material as LineMaterial)?.dispose();
            this.arcCizgi = null;
        }

        // Radius çizgisi
        if (this.radiusCizgi) {
            this.remove(this.radiusCizgi);
            (this.radiusCizgi.geometry as LineGeometry)?.dispose();
            (this.radiusCizgi.material as LineMaterial)?.dispose();
            this.radiusCizgi = null;
        }

        // Koordinat crosshair
        if (this.koordinatCrosshair) {
            this.remove(this.koordinatCrosshair);
            this.koordinatCrosshair.traverse((child) => {
                if ((child as THREE.Mesh).geometry) {
                    (child as THREE.Mesh).geometry.dispose();
                }
                if ((child as THREE.Mesh).material) {
                    ((child as THREE.Mesh).material as THREE.Material).dispose();
                }
            });
            this.koordinatCrosshair = null;
        }
    }

    // =====================================================================
    // ✅ DRAG & DROP ÖZELLİĞİ - CATIA TARZI SÜRÜKLENEBİLİR ÖLÇÜM
    // =====================================================================

    /**
     * ✅ SÜRÜKLEME BAŞLAT
     * Pointer olaylarını dinlemeye başla
     */
    suruklemeyiBaslat(canvas: HTMLCanvasElement, kamera: THREE.Camera, sahne: THREE.Scene): void {
        if (!this.draggable) return;

        // Pointer down handler
        this.onPointerDown = (event: PointerEvent) => {
            if (!this.draggable) return;

            // Raycast ile tıklanan objeyi kontrol et
            const raycaster = new THREE.Raycaster();
            const mouse = new THREE.Vector2();

            const rect = canvas.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, kamera);
            const intersects = raycaster.intersectObjects(this.children, true);

            if (intersects.length > 0) {
                this._isDragging = true;
                this.dragOffset.copy(intersects[0].point).sub(this.position);
                canvas.style.cursor = 'grabbing';
                event.preventDefault();
            }
        };

        // Pointer move handler
        this.onPointerMove = (event: PointerEvent) => {
            if (!this._isDragging) return;

            // 🎯 v3.3.6: Koordinat ölçümü için serbest taşıma (sadece yazı hareket eder)
            if (this.olcumTuru === 'koordinat') {
                // Serbest X-Y taşıma (nokta2 göreceli pozisyon)
                const xDelta = event.movementX * 0.3;
                const yDelta = event.movementY * -0.3;

                this.nokta2.x += xDelta;
                this.nokta2.y += yDelta;

                // Çizgiyi yeniden oluştur
                this.olcumTuruneGoreCiz();

                // Yazıyı güncelle - scale koruması yaziEkle içinde yapılıyor
                if (this.userData.yaziParams) {
                    const params = this.userData.yaziParams;
                    this.yaziEkle(
                        params.metin,
                        undefined,
                        params.fontSize,
                        params.textColor,
                        params.backgroundColor,
                        params.backgroundAlpha,
                        params.offsetFactor
                    );
                }

                event.preventDefault();
                return;
            }

            const raycaster = new THREE.Raycaster();
            const mouse = new THREE.Vector2();

            const rect = canvas.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, kamera);

            // Offset'i artır/azalt (Y ekseni boyunca)
            // 🎯 v2.0.5 FIX: Negatif offset da destekleniyor - her iki yöne sürüklenebilir
            const yDelta = event.movementY * -0.5;
            this.offsetMesafe = this.offsetMesafe + yDelta;

            // Çizgiyi yeniden oluştur
            this.olcumTuruneGoreCiz();

            // 🎯 v2.1.0: Yazıyı yeniden konumlandır - tüm parametreleri koru
            // 🎯 v3.3.4: Scale koruması yaziEkle içinde yapılıyor
            if (this.userData.yaziParams) {
                const params = this.userData.yaziParams;
                this.yaziEkle(
                    params.metin,
                    undefined,
                    params.fontSize,
                    params.textColor,
                    params.backgroundColor,
                    params.backgroundAlpha,
                    params.offsetFactor
                );
            }

            event.preventDefault();
        };

        // Pointer up handler
        this.onPointerUp = (event: PointerEvent) => {
            if (this._isDragging) {
                this._isDragging = false;
                canvas.style.cursor = 'grab';
            }
        };

        // Event listener'ları ekle
        canvas.addEventListener('pointerdown', this.onPointerDown);
        canvas.addEventListener('pointermove', this.onPointerMove);
        canvas.addEventListener('pointerup', this.onPointerUp);
        canvas.addEventListener('pointerleave', this.onPointerUp);
    }

    /**
     * ✅ SÜRÜKLEME DURDUR
     * Event listener'ları kaldır
     */
    suruklemeviDurdur(canvas: HTMLCanvasElement): void {
        if (this.onPointerDown) {
            canvas.removeEventListener('pointerdown', this.onPointerDown);
            this.onPointerDown = null;
        }
        if (this.onPointerMove) {
            canvas.removeEventListener('pointermove', this.onPointerMove);
            this.onPointerMove = null;
        }
        if (this.onPointerUp) {
            canvas.removeEventListener('pointerup', this.onPointerUp);
            canvas.removeEventListener('pointerleave', this.onPointerUp);
            this.onPointerUp = null;
        }
        this._isDragging = false;
    }

    /**
     * ✅ OFFSET GÜNCELLE - Dışarıdan erişim için
     * 🎯 v2.0.5 FIX: Negatif offset da destekleniyor - her iki yöne sürüklenebilir
     * 🎯 v2.1.0: Tüm yazı parametreleri ve scale korunuyor
     */
    offsetGuncelle(yeniOffset: number): void {
        this.offsetMesafe = yeniOffset;
        this.olcumTuruneGoreCiz();

        // 🎯 v2.1.0: Yazıyı tüm parametrelerle yeniden oluştur
        if (this.userData.yaziParams) {
            const params = this.userData.yaziParams;
            this.yaziEkle(
                params.metin,
                undefined,
                params.fontSize,
                params.textColor,
                params.backgroundColor,
                params.backgroundAlpha,
                params.offsetFactor
            );

            // 🎯 v2.1.0: Scale'i koru - hemen uygula
            if (this.sabitBoyut && this.sahne) {
                this.kameraYonDegisti(this, true);
            }
        }
    }

    /**
     * ✅ OFFSET SIFIRLA - Orijinal offset'e dön
     * 🎯 v2.1.0: Tüm yazı parametreleri ve scale korunuyor
     */
    offsetSifirla(): void {
        this.offsetMesafe = this.originalOffset;
        this.olcumTuruneGoreCiz();

        // 🎯 v2.1.0: Yazıyı tüm parametrelerle yeniden oluştur
        if (this.userData.yaziParams) {
            const params = this.userData.yaziParams;
            this.yaziEkle(
                params.metin,
                undefined,
                params.fontSize,
                params.textColor,
                params.backgroundColor,
                params.backgroundAlpha,
                params.offsetFactor
            );

            // 🎯 v2.1.0: Scale'i koru - hemen uygula
            if (this.sabitBoyut && this.sahne) {
                this.kameraYonDegisti(this, true);
            }
        }
    }

    // =====================================================================
    // ✅ MERKEZİ AYARLAR SİSTEMİ - localStorage'dan yükleme
    // =====================================================================

    /**
     * ✅ STATIC: Merkezi ayarlardan ölçüm konfigürasyonu al
     * v2.0.0 - DimensionSettings entegrasyonu ile
     * Yeni ölçüm oluşturulurken kullanılır
     */
    static getOlcumAyarlari(): {
        renkler: { cizgi: number, ok: number, yazi: string, extensionLine: number, centerMark: number, arcLine: number, radiusLine: number },
        cizgiKalinligi: number,
        offsetMesafe: number,
        yaziFontSize: number,
        yaziBackgroundAlpha: number
    } {
        // 🎯 CATIA/IRONCAD VARSAYILAN DEĞERLER
        // Siyah çizgiler, siyah yazı, profesyonel görünüm
        const varsayilanRenk = 0x000000; // 🎯 SİYAH
        const defaults = {
            renkler: {
                cizgi: varsayilanRenk,
                ok: varsayilanRenk,
                yazi: '#000000',          // 🎯 Siyah yazı
                extensionLine: varsayilanRenk,
                centerMark: varsayilanRenk,
                arcLine: varsayilanRenk,
                radiusLine: varsayilanRenk
            },
            cizgiKalinligi: 1.5,
            offsetMesafe: 15,          // 🎯 CATIA TARZI: Dışarı çıkan offset
            yaziFontSize: 12,
            yaziBackgroundAlpha: 0        // Arka plan yok (beyaz shadow var)
        };

        try {
            // v2.0.0 - Önce DimensionSettings'i kontrol et
            const dimensionSettings = DimensionSettings.getInstance();
            const s = dimensionSettings.getSettings();

            // DimensionSettings'den renk ve ayar al
            const cizgiRenk = s.colors.dimensionLine ?? s.colors.normal;
            const extRenk = s.colors.extensionLine;
            const yaziRenk = s.label?.textColor ?? s.colors.textColor ?? defaults.renkler.yazi;

            return {
                renkler: {
                    cizgi: cizgiRenk,
                    ok: cizgiRenk,
                    yazi: yaziRenk,
                    extensionLine: extRenk,
                    centerMark: cizgiRenk,
                    arcLine: cizgiRenk,
                    radiusLine: cizgiRenk
                },
                cizgiKalinligi: s.dimensionLine?.width ?? defaults.cizgiKalinligi,
                offsetMesafe: defaults.offsetMesafe, // Offset ayarı henüz DimensionSettings'de yok
                yaziFontSize: s.label?.fontSize ?? defaults.yaziFontSize,
                yaziBackgroundAlpha: s.label?.backgroundOpacity ?? defaults.yaziBackgroundAlpha
            };
        } catch (e) {
            // DimensionSettings hata - legacy localStorage'a fallback
        }

        // Legacy fallback
        try {
            const kaydedilen = localStorage.getItem('FikoThreeJSAyarlari');
            if (kaydedilen) {
                const ayarlar = JSON.parse(kaydedilen);
                // Çizgi rengi tüm çizgi elemanlar için kullanılır
                const cizgiRenk = FikoOlcumCizgisi.hexToInt(ayarlar.olcumCizgiRengi) ?? defaults.renkler.cizgi;
                // Yazı rengi ayrı ayarlanabilir (varsayılan: siyah)
                const yaziRenk = ayarlar.olcumYaziRengi ?? defaults.renkler.yazi;
                return {
                    renkler: {
                        cizgi: cizgiRenk,
                        ok: cizgiRenk,
                        yazi: yaziRenk,
                        extensionLine: cizgiRenk,
                        centerMark: cizgiRenk,
                        arcLine: cizgiRenk,
                        radiusLine: cizgiRenk
                    },
                    cizgiKalinligi: ayarlar.olcumCizgiKalinligi ?? defaults.cizgiKalinligi,
                    offsetMesafe: ayarlar.olcumOffsetMesafe ?? defaults.offsetMesafe,
                    yaziFontSize: ayarlar.olcumYaziFontSize ?? defaults.yaziFontSize,
                    yaziBackgroundAlpha: ayarlar.olcumYaziBackgroundAlpha ?? defaults.yaziBackgroundAlpha
                };
            }
        } catch (e) {
            // localStorage hata - varsayılanları kullan
        }

        return defaults;
    }

    /**
     * ✅ STATIC: HEX renk stringini integer'a çevir
     * "#FFD700" -> 0xFFD700
     */
    private static hexToInt(hex: string | undefined): number | null {
        if (!hex || typeof hex !== 'string') return null;
        // # işaretini kaldır ve integer'a çevir
        const cleanHex = hex.replace('#', '');
        const parsed = parseInt(cleanHex, 16);
        return isNaN(parsed) ? null : parsed;
    }

    /**
     * ✅ Ayarları uygula - Merkezi ayarlardan instance'a
     * Constructor'da veya sonradan çağrılabilir
     */
    ayarlariUygula(): void {
        const ayarlar = FikoOlcumCizgisi.getOlcumAyarlari();
        console.log('[FikoOlcumCizgisi] ayarlariUygula - yeni renk:', ayarlar.renkler.cizgi.toString(16));

        // Renkleri uygula
        this.renkler.cizgi = ayarlar.renkler.cizgi;
        this.renkler.ok = ayarlar.renkler.ok;
        this.renkler.yazi = ayarlar.renkler.yazi;
        this.renkler.extensionLine = ayarlar.renkler.extensionLine;
        this.renkler.centerMark = ayarlar.renkler.centerMark;
        this.renkler.arcLine = ayarlar.renkler.arcLine;
        this.renkler.radiusLine = ayarlar.renkler.radiusLine;

        // Parametreleri uygula
        this.cizgiKalinligi = ayarlar.cizgiKalinligi;
        this.offsetMesafe = ayarlar.offsetMesafe;
        this.originalOffset = ayarlar.offsetMesafe;

        // Çizimi yeniden oluştur
        this.olcumTuruneGoreCiz();

        // v2.0.0 - Sahneyi yeniden render et
        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz?.();
        }
    }
}
